import { useEffect, useMemo, useState } from "react";
import useStyle from "./style";
import { apiEmbaleiV3, apiIDW, apiLabs } from "../../Services/api";
import { store } from "../../store";
import styled, { css, keyframes } from "styled-components";
import { format } from "date-fns";

// Portes de pedido cadastrados na transportadora (campo order_size).
// A ordem aqui e a ordem exibida no resumo do painel.
const ORDER_SIZE_SUMMARY = [
  { key: "grande", label: "GRANDE" },
  { key: "medio", label: "MÉDIO" },
  { key: "pequeno", label: "PEQUENO" },
];

// Quantidade de pecas do pedido (o painel de Embarque so contava pedidos).
const getOrderItemCount = (order) => parseInt(order?.QuantityProduct, 10) || 0;

// Valor do pedido no ERP — vem como string decimal ("59.50").
const getOrderValue = (order) => parseFloat(order?.ValueOrder) || 0;

// Valor em reais, sem centavos: nas células do pivot o centavo só polui.
const formatOrderValue = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

// Classe CSS aplicada na linha da tabela conforme o porte da transportadora.
const ORDER_SIZE_ROW_CLASS = {
  pequeno: "sizePequeno",
  medio: "sizeMedio",
  grande: "sizeGrande",
};

const ORDER_TYPES =
  "2967,4410,4412,954,1153,4041,5440,4293,890,875,877,871";

// Pedido cancelado no ERP — fica fora da base do filtro por data, porque
// cancelamento não é atraso operacional.
const CANCELED_STATUS = 90;

// Status do ERP que alimentam cada coluna de pendentes.
//
// O 19 é novo. Antes o 22 significava "aguardando romaneio" e juntava os dois
// momentos — embalado e não embalado — então o dashboard precisava perguntar
// ao Embalei, pedido a pedido, em qual deles cada um estava. Agora o ERP
// separa: 22 parou em "aguardando embalagem" e 19 é quem já embalou e espera
// o embarque.
const STATUS_CLOSED = 1;          // AG. INÍCIO
const STATUS_IN_PICKING = 20;     // EM SEPARAÇÃO
const STATUS_AWAITING_PACKAGING = 22; // AG. EMBALAGEM
const STATUS_AWAITING_MANIFEST = 19;  // AG. EMBARQUE
const STATUS_AWAITING_LABEL = 23; // Em espera etiqueta (marketplaces)

// Nome do dia pro rótulo da previsão — índice = Date.getDay()
const WEEKDAY_NAMES = [
  "DOMINGO",
  "SEGUNDA",
  "TERÇA",
  "QUARTA",
  "QUINTA",
  "SEXTA",
  "SÁBADO",
];

// A API do IDW devolve no máximo 2000 registros por página. Sem percorrer as
// páginas a tela truncaria em silêncio (sem erro) num dia de volume alto.
const IDW_PAGE_SIZE = 2000;
const IDW_MAX_PAGES = 25; // trava de segurança: 50 mil pedidos

/**
 * Busca /orders percorrendo todas as páginas até vir uma incompleta.
 *
 * ATENÇÃO: `Page` é ZERO-INDEXADO — Page=0 é a primeira página, e é o que a
 * API usa quando o parâmetro é omitido. Começar em 1 descarta a primeira
 * página inteira, silenciosamente. As páginas são disjuntas.
 */
async function fetchAllOrderPages(params) {
  const allOrders = [];

  for (let page = 0; page < IDW_MAX_PAGES; page += 1) {
    const response = await apiIDW.get("/orders", {
      params: { ...params, Page: page },
    });
    const pageOrders = Array.isArray(response.data) ? response.data : [];
    allOrders.push(...pageOrders);
    if (pageOrders.length < IDW_PAGE_SIZE) break;
  }

  return allOrders;
}

const pulse = keyframes`
  0% {
    background: #fe0000;
  }

  50% {
    background: #fe0000;
  }

  100% {
    background: #fff;
  }
`;

export const Row = styled.p`
  ${({ status }) =>
    status === "collectionInTime" &&
    css`
      animation: ${pulse} 1s infinite ease-out;
      z-index: 1 !important;
    `}

  /* Transportadora expressa com pedido vencendo em 30 minutos. Pisca a faixa
     FALTAM 30M inteira — as quatro colunas juntas, mesmo as zeradas — porque
     o aviso é da transportadora, não de uma etapa isolada.

     Usa o MESMO ${"pulse"} da coleta que está a 1h de fechar, em vez de um
     keyframes próprio: é o pisca que a operação já reconhece de longe, e
     duplicar significaria dois piscas fora de sincronia na mesma tela. */
  ${({ $expressWarning }) =>
    $expressWarning &&
    css`
      z-index: 1 !important;
      .expressCell.faltam30 {
        animation: ${pulse} 1s infinite ease-out;
      }
    `}
`;

function Embarque() {
  const styles = useStyle();

  // Relógio ao vivo exibido no header (atualiza a cada segundo)
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [ordersAwaitingManifest, setOrderAwaitingManifest] = useState({ data: [] });
  const [ordersAwaitingPackaging, setOrdersAwaitingPackaging] = useState({ data: [] });
  const [ordersInPicking, setOrderInPicking] = useState({ data: [] });
  const [ordersClosed, setOrderClosed] = useState({ data: [] });
  const [ordersShipped, setOrderShipped] = useState({ data: [] });
  // Só no modo filtrado: expedição era o dia filtrado, mas o pedido saiu DEPOIS.
  // Não é "envio com atraso do dia" — no dia filtrado ele simplesmente não saiu.
  // Entra em EXPEDIR NO DIA e conta só na coluna TOTAL.
  const [ordersShippedAfterDate, setOrdersShippedAfterDate] = useState({ data: [] });
  // Status 23 = "aguardando etiqueta" (marketplaces) — só atraso + previsão
  const [ordersAwaitingLabel, setOrdersAwaitingLabel] = useState({ data: [] });
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [modal, setModal] = useState(false);
  const [dataModal, setDataModal] = useState({
    name: "",
    supplier_name: "",
    time_collection: 0,
    status: "",
    data: [],
    tabs: null,
    activeTab: 0,
  });
  // Busca + filtro de status dentro do modal de listagem de pedidos
  const [modalSearch, setModalSearch] = useState("");
  const [modalStatusFilter, setModalStatusFilter] = useState("");
  // Sub-aba dentro da "Visão Geral" — 'day' | 'status' | 'carrier' | 'size' | 'value'
  const [overviewTab, setOverviewTab] = useState("day");
  // Como a listagem de pedidos é exibida — 'list' (tabela) | 'analytics' (painel)
  const [listView, setListView] = useState("list");

  // Recalcular data de embarque: linha individual em loading + progresso do bulk
  const [recalcRow, setRecalcRow] = useState(null); // IDOrder da linha em recalculo
  const [recalcBulk, setRecalcBulk] = useState({ active: false, current: 0, total: 0 });

  // Trigger pra forçar refetch dos dados (getOrders) após recálculo
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // ── FILTRO POR DATA (opcional) ─────────────────────────────────────────────
  // Vazio = comportamento padrão (pendentes de hoje + enviados hoje).
  // Com data D, a tela inteira é remontada com:
  //   · pendentes cuja DATA DE EXPEDIÇÃO (embarque) é exatamente D
  //   · pedidos cuja DATA DE ENVIO é exatamente D
  // Um pedido que aparece nos dois conjuntos conta só como ENVIADO.
  // filterDateInput = o que está digitado; appliedFilterDate = o filtro em vigor.
  const [filterDateInput, setFilterDateInput] = useState("");
  const [appliedFilterDate, setAppliedFilterDate] = useState(null);
  const isDateFilterActive = !!appliedFilterDate;

  // O painel do filtro fica escondido — a tela é de TV. Quem precisa abre pelo
  // botão discreto no canto inferior direito, que apaga sozinho depois de um
  // tempo (igual ao alternador de visão do dashboard de ondas).
  const [showDateFilterPanel, setShowDateFilterPanel] = useState(false);
  const [dimFilterButton, setDimFilterButton] = useState(false);

  useEffect(() => {
    // Com painel aberto ou filtro ativo o botão continua visível: o operador
    // precisa enxergar que a tela não está mostrando o dia de hoje.
    if (showDateFilterPanel || isDateFilterActive) {
      setDimFilterButton(false);
      return undefined;
    }
    const timer = setTimeout(() => setDimFilterButton(true), 4000);
    return () => clearTimeout(timer);
  }, [showDateFilterPanel, isDateFilterActive]);

  function applyDateFilter() {
    if (!filterDateInput) return;
    setAppliedFilterDate(filterDateInput);
  }

  function clearDateFilter() {
    setFilterDateInput("");
    setAppliedFilterDate(null);
    setShowDateFilterPanel(false);
  }

  // Recursos restritos da tela: recalcular data de embarque, filtro por data e
  // a aba "Por Tipo e Valor". Comparação case-insensitive contra o `name` do
  // profile — o nome precisa bater exatamente com o login.
  // A rota é pública (TV sem login) — sem profile no store, ninguém vê nada.
  //
  // Lista ÚNICA de propósito: o recálculo tinha a própria cópia e ficou pra
  // trás quando esta foi atualizada, deixando johnny e fabio sem o botão.
  const RESTRICTED_FEATURE_USERS = ['master', 'fabio', 'romulo', 'johnny'];
  const isRestrictedFeatureUser = (() => {
    try {
      const name = String(store.getState()?.user?.profile?.name || '').toLowerCase();
      return RESTRICTED_FEATURE_USERS.includes(name);
    } catch (_) {
      return false;
    }
  })();
  // Quatro recursos com a mesma audiência hoje, nomeados separadamente porque
  // são decisões independentes — se um dia um deles abrir pra mais gente,
  // basta trocar a origem daquele flag.
  const canRecalcPermission = isRestrictedFeatureUser;
  const canUseDateFilter = isRestrictedFeatureUser;
  const canSeeOrderValues = isRestrictedFeatureUser;
  const canSeeAnalytics = isRestrictedFeatureUser;

  // Travas: sem permissão, nem a aba de valores nem a visão analítica ficam
  // ativas, mesmo que o estado chegue nelas por outro caminho.
  useEffect(() => {
    if (!canSeeOrderValues && overviewTab === "value") setOverviewTab("size");
  }, [canSeeOrderValues, overviewTab]);

  useEffect(() => {
    if (!canSeeAnalytics && listView === "analytics") setListView("list");
  }, [canSeeAnalytics, listView]);

  const [totals, setTotals] = useState({
    orders: 0,
    ordersAwaitingManifest: 0,
    ordersAwaitingPackaging: 0,
    ordersInPicking: 0,
    ordersClosed: 0,
    ordersShipped: 0,
    ordersEarlyShipping: 0,
    ordersLateShipping: 0,
    ordersOnTimeShipping: 0,
    ordersDelayed: 0,
    ordersNextDay: 0,
    ordersNext48h: 0,
    ordersDeliveryAlert: 0,
    ordersNoShipDate: 0,
    ordersShippedAfterDate: 0,
    dataOrdersNextDay: [],
    dataOrdersShippedAfterDate: [],
    dataOrdersNext48h: [],
    dataOrdersDeliveryAlert: [],
    dataOrdersNoShipDate: [],
    dataOrdersAll: [],
    ordersBySize: {},
    totalItems: 0,
    delayedByColumn: {},
  });

  const [suppliers, setSuppliers] = useState([]);
  const [suppliersData, setSuppliersData] = useState({ data: [] });

  // Feriados que BLOQUEIAM coleta — usado pra excluir da Visão Geral (junto com sáb/dom).
  // Set de YYYY-MM-DD.
  const [holidayDates, setHolidayDates] = useState(() => new Set());
  useEffect(() => {
    async function fetchHolidays() {
      try {
        const year = new Date().getFullYear();
        // Ano corrente + próximo — cobre viradas de ano na janela de 7 dias
        const [r1, r2] = await Promise.all([
          apiLabs.get(`feriados?ano=${year}`),
          apiLabs.get(`feriados?ano=${year + 1}`),
        ]);
        const set = new Set();
        [...(r1.data || []), ...(r2.data || [])].forEach((h) => {
          if (h && h.blocks_shipping !== false && h.date) {
            set.add(String(h.date).slice(0, 10));
          }
        });
        setHolidayDates(set);
      } catch (err) {
        console.warn('fetch feriados falhou (fallback: só sáb/dom serão excluídos):', err?.message);
      }
    }
    fetchHolidays();
  }, []);

  // Um dia é "bloqueado" (não útil) se for sábado, domingo ou feriado com blocks_shipping.
  function isBlockedDate(date) {
    if (!date) return false;
    const dow = date.getDay();
    if (dow === 0 || dow === 6) return true; // domingo ou sábado
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return holidayDates.has(`${y}-${m}-${d}`);
  }

  // ── HELPERS DE DATA ──────────────────────────────────────────────────────
  // Timestamps do ERP vêm com "Z" mas já são horário local correto — não converter.
  function parseApiLocalDate(dateString) {
    if (!dateString) return null;
    if (dateString instanceof Date) return new Date(dateString);

    const match = String(dateString).match(
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/
    );

    if (!match) return new Date(dateString);

    const [, year, month, day, hour, minute, second] = match;

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    );
  }

  function startOfDay(dateValue) {
    const date = parseApiLocalDate(dateValue);
    if (!date) return null;
    date.setHours(0, 0, 0, 0);
    return date;
  }

  // "YYYY-MM-DD" (valor do <input type="date">) → Date local à meia-noite.
  // Não usar new Date("YYYY-MM-DD"): o JS interpreta como UTC e volta um dia.
  function parseInputDate(inputValue) {
    if (!inputValue) return null;
    const [year, month, day] = String(inputValue).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  // "YYYY-MM-DD" → "DD/MM" para os rótulos do header
  function formatInputDateLabel(inputValue) {
    const date = parseInputDate(inputValue);
    if (!date) return "";
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  }

  function getHourDecimal(dateValue) {
    const date = parseApiLocalDate(dateValue);
    if (!date) return 0;
    return date.getHours() + date.getMinutes() / 60 + date.getSeconds() / 3600;
  }

  // Converte TIME do backend ('HH:MM:SS' | 'HH:MM' | inteiro legado) → decimal de horas
  function timeToDecimal(value) {
    if (value == null || value === "") return 0;
    if (typeof value === "number") return value;
    const m = String(value).match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (m) return Number(m[1]) + Number(m[2]) / 60;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }

  // Decimal de horas → "HH:MM"
  function formatHourLabel(decimal) {
    const n = Number(decimal) || 0;
    const h = Math.floor(n);
    const min = Math.round((n - h) * 60);
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
  }

  // Data de expedição/embarque calculada pelo backend (data + hora da última
  // coleta). Usada para TODOS os pedidos, sem exceção.
  function getLimitDate(order) {
    return order?.ShippingEstimateHandlingLimitDate
      ? parseApiLocalDate(order.ShippingEstimateHandlingLimitDate)
      : null;
  }

  // Próximo dia útil (pula sábado/domingo) — sexta → segunda
  function nextBusinessDay(fromValue) {
    const d = startOfDay(fromValue || new Date());
    do {
      d.setDate(d.getDate() + 1);
    } while (d.getDay() === 0 || d.getDay() === 6);
    return d;
  }

  // ── EMBALEI: quem já foi embalado ─────────────────────────────────────────
  // A API de lote aceita até 100 números de pedido por chamada. Antes era uma
  // chamada POR PEDIDO: eram mais de mil requisições por ciclo
  // e o bloco dominava o carregamento inteiro. Agora são 13.
  const EMBALEI_BATCH_SIZE = 100;
  // Espaço entre os lotes. A API aguenta a sequência, mas disparar sem pausa
  // é o que costuma acordar o rate limit do host.
  const EMBALEI_BATCH_DELAY_MS = 200;

  /**
   * "2026-10-02T01:05:30.000Z" → Date no fuso local.
   *
   * ATENÇÃO, é a exceção do projeto: todo timestamp do ERP chega com "Z" mas
   * já é horário local, e por isso o resto do arquivo usa parseApiLocalDate,
   * que lê literalmente. A API do Embalei manda UTC DE VERDADE — lida
   * literalmente, a embalagem aparecia 3 horas adiantada.
   */
  function parseEmbaleiDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  /**
   * Devolve um Map numeroDoPedido → dados de embalagem, só dos que o Embalei
   * encontrou. `encontrado: false` significa "ainda não embalado".
   *
   * A chave é order.Order (o "VX..."), não IDOrder — é o que a API consulta.
   *
   * Lotes em série e espaçados, não em paralelo: uma rajada de requisições
   * simultâneas é o que derruba o rate limit do host, e o ganho sobre as mais
   * de mil chamadas de antes já veio todo do lote.
   */
  async function fetchPackagingStatus(orders = []) {
    const packagedByOrder = new Map();
    // Set: as listas chegam concatenadas e podem repetir o mesmo pedido.
    // Número duplicado gastaria espaço do lote sem trazer nada.
    const orderNumbers = [
      ...new Set(orders.map((order) => order.Order).filter(Boolean)),
    ];

    for (let i = 0; i < orderNumbers.length; i += EMBALEI_BATCH_SIZE) {
      // Pausa ANTES do lote, menos no primeiro: esperar depois do último
      // atrasaria o carregamento da tela sem nenhuma chamada pela frente.
      if (i > 0) {
        await new Promise((resolve) => setTimeout(resolve, EMBALEI_BATCH_DELAY_MS));
      }
      const numeros = orderNumbers.slice(i, i + EMBALEI_BATCH_SIZE).join(",");
      try {
        const response = await apiEmbaleiV3.get("/pedidos", { params: { numeros } });
        (response?.data?.resultados || []).forEach((item) => {
          if (item?.encontrado && item?.pedido) {
            packagedByOrder.set(item.consulta, item.pedido);
          }
        });
      } catch (error) {
        // Um lote que falha derruba EMBALEI_BATCH_SIZE pedidos de uma vez,
        // não um só como antes. Tem que aparecer.
        console.error(
          `[Embarque] lote do Embalei falhou (${i + 1}–${i + EMBALEI_BATCH_SIZE}) — ` +
          "esses pedidos ficam sem data de embalagem neste ciclo:",
          error?.response?.status || error?.message
        );
      }
    }

    return packagedByOrder;
  }

  // ── ENTREGA EXPRESSA (prazo em horas) ─────────────────────────────────────
  // A transportadora é expressa quando o cadastro traz time_expedition_hours
  // em vez de time_expedition. Para ela o prazo é contado em HORAS a partir da
  // criação do pedido, então o dia inteiro não serve como unidade: um pedido
  // que vence às 16:30 precisa aparecer como atrasado às 16:31, não amanhã.
  //
  // "00:00:00" é descartado de propósito: é o default de uma coluna TIME, e
  // tratá-lo como valor jogaria TODAS as transportadoras no layout expresso.
  function isExpressSupplier(supplierLike) {
    const hours = supplierLike?.time_expedition_hours;
    if (!hours) return false;
    return !/^0{1,2}:00(:00)?$/.test(String(hours).trim());
  }

  // Minutos que faltam para a expedição virar atraso.
  const EXPRESS_WARNING_MINUTES = 30;

  // Classifica o pedido expresso comparando DATA + HORA contra o agora. O
  // resto do dashboard compara dias truncados (startOfDay), o que zeraria
  // qualquer atraso de uma promessa de 3 horas.
  //
  // A hora sai de ShippingEstimateHandlingLimitDate, que para a expressa o
  // backend grava como criação + time_expedition_hours.
  function classifyExpressOrder(order, nowMs) {
    const limitDate = getLimitDate(order);
    if (!limitDate) return "noPrazo";
    const minutesLeft = (limitDate.getTime() - nowMs) / 60000;
    if (minutesLeft <= 0) return "atrasado";
    if (minutesLeft <= EXPRESS_WARNING_MINUTES) return "faltam30";
    return "noPrazo";
  }

  // Colunas operacionais que ganham a quebra por urgência na linha expressa.
  const EXPRESS_COLUMNS = [
    "ordersClosed",
    "ordersInPicking",
    "ordersAwaitingPackaging",
    "ordersAwaitingManifest",
  ];

  const EXPRESS_STATUS_ROWS = [
    { key: "atrasado", label: "ATRASADO" },
    { key: "faltam30", label: `FALTAM ${EXPRESS_WARNING_MINUTES}M` },
    { key: "noPrazo", label: "NO PRAZO" },
  ];

  function emptyExpressBuckets() {
    const buckets = {};
    EXPRESS_COLUMNS.forEach((column) => {
      buckets[column] = { atrasado: 0, faltam30: 0, noPrazo: 0 };
    });
    return buckets;
  }

  // Há pedido pendente com data de embarque marcada para este dia?
  function hasPendingOrdersOn(dayMs, pendingLists) {
    return pendingLists.some((list) =>
      (list?.data || []).some((order) => {
        const limitDate = getLimitDate(order);
        return !!limitDate && startOfDay(limitDate).getTime() === dayMs;
      })
    );
  }

  // DIA DA PREVISÃO — o que alimenta o card "PREVISÃO AMANHÃ/SEGUNDA".
  //
  // Era sempre nextBusinessDay(), então na sexta pulava direto pra segunda e
  // todo pedido com embarque no sábado escorregava pro balde "+48H". Agora há
  // transportadora operando no sábado (works_saturday no cadastro), e o sábado
  // passa a ser um dia de previsão válido.
  //
  // Quem decide é o próprio dado, não o cadastro: só vira previsão de sábado
  // se existir pedido com expedição marcada pro sábado. Sem nenhum, segue pra
  // segunda como antes — numa semana em que a transportadora de sábado não tem
  // carga, o card não fica zerado. Domingo continua fora em qualquer caso.
  function resolveForecastDay(pendingLists) {
    const day = startOfDay(new Date());
    // No máximo 7 saltos: um dia de semana sempre encerra o laço.
    for (let step = 0; step < 7; step += 1) {
      day.setDate(day.getDate() + 1);
      const weekday = day.getDay();
      if (weekday === 0) continue; // domingo: nunca é dia de previsão
      if (weekday !== 6) return new Date(day); // seg–sex: igual a antes
      // Sábado: só entra se houver pedido marcado pra ele.
      if (hasPendingOrdersOn(day.getTime(), pendingLists)) return new Date(day);
    }
    return nextBusinessDay(new Date());
  }

  // Compara envio x data de embarque: antecipado / no prazo / atrasado
  /** Zera minutos e segundos — a unidade de comparação da expressa. */
  function startOfHour(value) {
    const d = value instanceof Date ? new Date(value) : parseApiLocalDate(value);
    if (!d) return null;
    d.setMinutes(0, 0, 0);
    return d;
  }

  // `express` troca a unidade da comparação. Na transportadora de dias o que
  // importa é o DIA do envio contra o dia da expedição — sair às 08h ou às
  // 18h do dia certo é igualmente no prazo. Na expressa o prazo é de horas,
  // então a comparação é de DATA + HORA: sair às 16h31 de um prazo das 16h30
  // é atraso, e truncar no dia transformaria isso em "no prazo".
  function getShipmentTiming(shippingDate, limitDate, express = false) {
    if (!shippingDate || !limitDate) return null;

    if (express) {
      const shippedAt = parseApiLocalDate(shippingDate);
      const limitAt = limitDate instanceof Date ? limitDate : parseApiLocalDate(limitDate);
      if (!shippedAt || !limitAt) return null;
      // Mesma regra de sempre, só que a unidade é a HORA em vez do dia:
      // saiu na hora do prazo = no prazo, numa hora anterior = antecipado,
      // depois = atrasado. Comparar o instante exato zeraria o "no prazo"
      // para sempre — bater o milissegundo do prazo não acontece.
      const shippedHour = startOfHour(shippedAt);
      const limitHour = startOfHour(limitAt);
      if (shippedHour.getTime() === limitHour.getTime()) return "on_time";
      if (shippedHour.getTime() < limitHour.getTime()) return "early";
      return "late";
    }

    const shipped = startOfDay(shippingDate);
    const limit = startOfDay(limitDate);
    if (!shipped || !limit) return null;
    if (shipped.getTime() === limit.getTime()) return "on_time";
    if (shipped.getTime() < limit.getTime()) return "early";
    return "late";
  }

  // ── HELPERS DE TRANSPORTADORA ────────────────────────────────────────────
  function getOrderSupplierKey(order) {
    return order?.SupplierNameCorporateName ?? "";
  }

  function getUniqueSupplierRows(suppliersSource) {
    const rows = [];

    // Somente transportadoras ativas no sistema
    suppliersSource
      .filter((sup) => sup.is_active === true)
      .forEach((sup) => {
        const exists = rows.find(
          (item) =>
            item.name === sup.name &&
            item.time_collection === sup.time_collection
        );

        if (!exists) {
          rows.push({
            name: sup.name,
            // Porte do pedido que a transportadora leva (pequeno | medio | grande | null)
            order_size: sup.order_size || null,
            time_cut: sup.time_cut,
            time_collection: sup.time_collection,
            time_expedition: sup.time_expedition,
            // Prazo em horas (entrega expressa). Quando preenchido, a linha
            // quebra em ATRASADO / FALTAM 30M / NO PRAZO.
            time_expedition_hours: sup.time_expedition_hours || null,
            expressByStatus: emptyExpressBuckets(),
            orders: 0,
            ordersAwaitingManifest: 0,
            ordersInPicking: 0,
            ordersClosed: 0,
            ordersShipped: 0,
            ordersEarlyShipping: 0,
            ordersLateShipping: 0,
            ordersOnTimeShipping: 0,
            ordersAwaitingPackaging: 0,
            ordersDelayed: 0,
            ordersNextDay: 0,
            dataOrdersAwaitingManifest: [],
            dataOrdersInPicking: [],
            dataOrdersClosed: [],
            dataOrdersShipped: [],
            dataOrdersEarlyShipping: [],
            dataOrdersLateShipping: [],
            dataOrdersOnTimeShipping: [],
            dataOrdersAwaitingPackaging: [],
            dataOrdersDelayed: [],
            dataOrdersNextDay: [],
            // Expedição era o dia filtrado, mas o pedido saiu depois — entra
            // em EXPEDIR NO DIA e conta só na coluna TOTAL.
            ordersShippedAfterDate: 0,
            dataOrdersShippedAfterDate: [],
            // Atrasados por coluna de status — alimenta o "(n)" vermelho ao
            // lado do número de cada etapa. Chave = counterField da coluna.
            delayedByColumn: {},
          });
        }
      });

    return rows.sort((a, b) => {
      if (a.time_collection !== b.time_collection) {
        return a.time_collection - b.time_collection;
      }
      return a.name.localeCompare(b.name);
    });
  }

  function getSupplierConfigsByOrder(order, suppliersSource) {
    const supplierKey = getOrderSupplierKey(order);

    // Só configs ATIVAS. O getUniqueSupplierRows já filtrava por is_active ao
    // montar as linhas da tabela, mas esta função não — e as duas precisam
    // concordar sobre quais configs existem.
    //
    // Quando não concordavam, uma transportadora com dois cadastros (um ativo
    // e um inativo) perdia pedidos sem avisar: aqui a inativa era escolhida,
    // lá não havia linha com aquele time_collection, o findRow devolvia null
    // e o pedido sumia da tabela inteira.
    const active = suppliersSource.filter((s) => s.is_active === true);

    let configs = active
      .filter((s) => (s.supplier_name ?? "") === supplierKey)
      .sort((a, b) => a.time_collection - b.time_collection);

    if (configs.length) return configs;

    // fallback para casos sem supplier_name no ERP
    if (!supplierKey) {
      configs = active
        .filter((s) => s.name === "TRANSFERÊNCIA")
        .sort((a, b) => a.time_collection - b.time_collection);
    }

    return configs;
  }

  // Janela de coleta de um pedido pendente.
  //
  // Regra:
  //   - Se o pedido embarca HOJE (ou está atrasado), ele cai na PRÓXIMA
  //     coleta ainda pendente baseado na HORA ATUAL. Antes das 09h, todos
  //     os pedidos vão pra 09h; depois das 09h, migram pra 16h; após 16h,
  //     ficam na 16h (última do dia).
  //   - Se o pedido embarca em um dia FUTURO, cai na PRIMEIRA coleta do dia
  //     (o operador tem o dia inteiro pra separar antes da primeira coleta).
  function getConfigForLimit(order, limitDate, suppliersSource) {
    const configs = getSupplierConfigsByOrder(order, suppliersSource);
    if (!configs.length) return null;
    if (configs.length === 1) return configs[0];

    const today0 = startOfDay(new Date()).getTime();
    const limit0 = startOfDay(limitDate).getTime();

    if (limit0 <= today0) {
      // Hoje ou atrasado → próxima coleta pendente (por hora atual)
      const nowH = getHourDecimal(new Date());
      const next = configs.find((c) => c.time_collection >= nowH);
      return next || configs[configs.length - 1];
    }

    // Dia futuro → primeira coleta do dia (todos começam ali)
    return configs[0];
  }

  // Janela de coleta de um pedido enviado = definida pela HORA DE ENVIO
  function getShippedCutoffConfig(order, suppliersSource) {
    const configs = getSupplierConfigsByOrder(order, suppliersSource);
    if (!configs.length) return null;
    if (configs.length === 1) return configs[0];

    const shippedHour = getHourDecimal(order.ShippingDate);
    const targetConfig = configs.find((config) => config.time_collection >= shippedHour);
    return targetConfig || configs[configs.length - 1];
  }

  // ── BUSCA DE DADOS ─────────────────────────────────────────────────────────
  useEffect(() => {
    let interval;

    async function getOrders() {
      try {
        setLoading(true);

        const now = new Date();
        const offset = now.getTimezoneOffset() * 60000;
        const localISODate = new Date(now - offset).toISOString().split("T")[0];

        let rawOrdersAwaitingManifest = [];
        let rawOrdersAwaitingPackaging = [];
        let rawOrdersInPicking = [];
        let rawOrdersClosed = [];
        let rawOrdersShipped = [];
        let rawOrdersAwaitingLabel = [];
        let rawOrdersShippedAfterDate = [];

        const carriersRequest = apiLabs.get("carrier");

        if (appliedFilterDate) {
          // ── MODO FILTRADO ────────────────────────────────────────────────
          // Três origens:
          //   · APIs de status  → tudo que ainda está pendente, de qualquer data
          //   · expedição == D  → o que era pra sair no dia filtrado
          //   · envio     == D  → o que efetivamente saiu no dia filtrado
          //
          // ENVIADO é o conjunto do envio, inteiro, classificado em
          // antecipado/no prazo/atrasado pela própria expedição de cada pedido.
          //
          // Do lado da expedição, o que NÃO saiu no dia se divide em:
          //   · saiu ANTES  → antecipado, fica fora do filtro
          //   · não saiu    → atrasado de verdade, vai pra coluna do status
          //   · saiu DEPOIS → EXPEDIR NO DIA, contando só no TOTAL
          const [
            ordersDueOnDate,
            ordersSentOnDate,
            awaitingManifest,
            awaitingPackaging,
            inPicking,
            closed,
            awaitingLabel,
          ] = await Promise.all([
            fetchAllOrderPages({
              ShippingHandlingLimitDateFrom: appliedFilterDate,
              ShippingHandlingLimitDateTo: appliedFilterDate,
              Budget: 0,
              IDOrderType: ORDER_TYPES,
            }),
            fetchAllOrderPages({
              ShippingDateFrom: appliedFilterDate,
              ShippingDateTo: appliedFilterDate,
              Budget: 0,
              IDOrderType: ORDER_TYPES,
            }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_MANIFEST), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_PACKAGING), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_IN_PICKING), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_CLOSED), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_LABEL), IDOrderType: ORDER_TYPES }),
          ]);

          const filterDayTime = startOfDay(parseInputDate(appliedFilterDate)).getTime();
          const dayTimeOf = (value) => {
            const dayStart = startOfDay(value);
            return dayStart ? dayStart.getTime() : null;
          };

          rawOrdersShipped = ordersSentOnDate;
          const sentOnDateIds = new Set(ordersSentOnDate.map((order) => order.IDOrder));

          // Pendentes: não enviados com expedição até o dia filtrado. Expedição
          // futura fica de fora — ainda não era pra ter saído.
          const pendingOrdersById = new Map();
          const collectPendingOrder = (order) => {
            if (Number(order.IDStatusOrder) === CANCELED_STATUS) return;
            if (order.ShippingDate) return;
            const limitDayTime = dayTimeOf(order.ShippingEstimateHandlingLimitDate);
            if (limitDayTime == null || limitDayTime > filterDayTime) return;
            if (!pendingOrdersById.has(order.IDOrder)) {
              pendingOrdersById.set(order.IDOrder, order);
            }
          };
          [awaitingManifest, awaitingPackaging, inPicking, closed, awaitingLabel, ordersDueOnDate]
            .forEach((list) => list.forEach(collectPendingOrder));

          pendingOrdersById.forEach((order) => {
            const status = Number(order.IDStatusOrder);
            if (status === STATUS_IN_PICKING) rawOrdersInPicking.push(order);
            else if (status === STATUS_AWAITING_PACKAGING) rawOrdersAwaitingPackaging.push(order);
            else if (status === STATUS_AWAITING_MANIFEST) rawOrdersAwaitingManifest.push(order);
            else if (status === STATUS_AWAITING_LABEL) rawOrdersAwaitingLabel.push(order);
            else rawOrdersClosed.push(order); // 1 (fechado) e 0 (aberto)
          });

          rawOrdersShippedAfterDate = ordersDueOnDate.filter((order) => {
            if (sentOnDateIds.has(order.IDOrder)) return false;
            if (Number(order.IDStatusOrder) === CANCELED_STATUS) return false;
            if (!order.ShippingDate) return false;
            const shippedDayTime = dayTimeOf(order.ShippingDate);
            return shippedDayTime != null && shippedDayTime > filterDayTime;
          });
        } else {
          // ── MODO PADRÃO (hoje) ───────────────────────────────────────────
          // Pendentes de qualquer data + o que foi enviado hoje.
          const [
            awaitingManifest,
            awaitingPackaging,
            inPicking,
            closed,
            shipped,
            awaitingLabel,
          ] = await Promise.all([
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_MANIFEST), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_PACKAGING), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_IN_PICKING), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_CLOSED), IDOrderType: ORDER_TYPES }),
            fetchAllOrderPages({
              ShippingDateFrom: localISODate,
              ShippingDateTo: localISODate,
              Budget: 0,
              IDOrderType: ORDER_TYPES,
            }),
            fetchAllOrderPages({ IDStatusOrder: String(STATUS_AWAITING_LABEL), IDOrderType: ORDER_TYPES }),
          ]);

          rawOrdersAwaitingManifest = awaitingManifest;
          rawOrdersAwaitingPackaging = awaitingPackaging;
          rawOrdersInPicking = inPicking;
          rawOrdersClosed = closed;
          rawOrdersShipped = shipped;
          rawOrdersAwaitingLabel = awaitingLabel;
        }

        const carriersResponse = await carriersRequest;

        // time_cut/time_collection agora são TIME ('HH:MM:SS') → normaliza p/ decimal
        const carrierItems = (carriersResponse.data.items || []).map((item) => ({
          ...item,
          time_collection: timeToDecimal(item.time_collection),
          time_cut: timeToDecimal(item.time_cut),
        }));

        // Diagnóstico da entrega expressa: a linha de urgência só aparece pra
        // quem tem time_expedition_hours. Sem isso, a transportadora cai no
        // layout antigo sem avisar — e não dá pra saber, olhando a tela, se o
        // campo não foi cadastrado ou se a API não devolveu.
        const expressCarriers = carrierItems.filter((item) => item.time_expedition_hours);
        if (expressCarriers.length) {
          console.info(
            `[Embarque] expressas: ${expressCarriers
              .map((item) => `${item.name}=${item.time_expedition_hours}`)
              .join(", ")}`
          );
        } else {
          const hasField = carrierItems.some((item) => "time_expedition_hours" in item);
          console.info(
            hasField
              ? "[Embarque] nenhuma transportadora com time_expedition_hours preenchido — todas no modo dias."
              : "[Embarque] a API /carrier não devolveu o campo time_expedition_hours."
          );
        }

        // Consulta ao Embalei — agora só pela DATA de embalagem.
        //
        // Antes ela decidia a coluna: o status 22 juntava embalado e não
        // embalado, e era o Embalei que separava os dois. Com o 19 vindo do
        // ERP, a separação já chega pronta e a consulta ficou sendo só
        // enriquecimento da coluna DATA EMBALAGEM dos modais.
        //
        // Isso tira os não embalados da consulta: quem está em 22 nunca foi
        // encontrado no Embalei mesmo, então eram justamente as buscas que
        // voltavam vazias. Os três grupos vão na mesma passada porque a API é
        // em lote e o fetchPackagingStatus deduplica os números.
        const packagedByOrder = await fetchPackagingStatus([
          ...rawOrdersAwaitingManifest,
          ...rawOrdersShipped,
          ...rawOrdersShippedAfterDate,
        ]);

        const markPackagedAt = (order) => {
          order._packagedAt = packagedByOrder.get(order.Order)?.embaladoEm || null;
        };
        rawOrdersAwaitingManifest.forEach(markPackagedAt);
        rawOrdersShipped.forEach(markPackagedAt);
        rawOrdersShippedAfterDate.forEach(markPackagedAt);

        setOrderAwaitingManifest({ data: rawOrdersAwaitingManifest });
        setOrdersAwaitingPackaging({ data: rawOrdersAwaitingPackaging });
        setOrderInPicking({ data: rawOrdersInPicking });
        setOrderClosed({ data: rawOrdersClosed });
        setOrderShipped({ data: rawOrdersShipped });
        setOrdersAwaitingLabel({ data: rawOrdersAwaitingLabel });
        setOrdersShippedAfterDate({ data: rawOrdersShippedAfterDate });
        setSuppliersData({ data: carrierItems });
      } catch (error) {
        console.error("Erro ao buscar pedidos:", error);
      } finally {
        setLoading(false);
      }
    }

    getOrders();
    // Refresh a cada 3 minutos (3 * 60 * 1000 = 180000ms)
    interval = setInterval(getOrders, 180000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger, appliedFilterDate]);

  // ── MONTAGEM DO DASHBOARD (bucketização por data de embarque) ──────────────
  useEffect(() => {
    if (!suppliersData?.data?.length) return;

    const baseSuppliers = getUniqueSupplierRows(suppliersData.data);
    const today = startOfDay(new Date());
    // Referência única do ciclo para as faixas da expressa — classificar cada
    // pedido com um `new Date()` próprio deixaria a conta inconsistente dentro
    // do mesmo ciclo.
    const nowMs = Date.now();
    // Sábado entra na previsão quando tem pedido — ver resolveForecastDay.
    const nbd = resolveForecastDay([
      ordersClosed,
      ordersInPicking,
      ordersAwaitingManifest,
      ordersAwaitingPackaging,
      ordersAwaitingLabel,
    ]);

    // ── FILTRO POR DATA ──────────────────────────────────────────────────────
    // As listas já chegam prontas da busca: no modo filtrado elas são a união
    // de "expedição == D" com "envio == D", deduplicada e sem cancelados. Aqui
    // No modo filtrado os pendentes têm expedição ATÉ o dia filtrado (o próprio
    // dia e os anteriores), então o atraso é medido pedido a pedido contra
    // hoje, e não pelo dia do filtro.
    const filterDay = appliedFilterDate
      ? startOfDay(parseInputDate(appliedFilterDate))
      : null;
    // forecast agora armazena arrays completos pros modais clicáveis
    const forecast = { next48h: 0, dataNext48h: [] };
    // Pedidos onde a data DE ENTREGA (ShippingEstimateDate) é anterior à data
    // limite de embarque (ShippingEstimateHandlingLimitDate) — só pra pedidos
    // NÃO marketplace. Alerta operacional: prazo apertado / risco de atraso.
    const deliveryAlertOrders = [];
    // Pedidos SEM data de embarque calculada (ShippingEstimateHandlingLimitDate
    // vazia). Antes eram descartados silenciosamente e não apareciam em nenhum
    // quadrante — agora viram alerta pra recalcular.
    const noShipDateOrders = [];

    function isDeliveryBeforeShipping(order) {
      const delivery = order?.ShippingEstimateDate ? startOfDay(order.ShippingEstimateDate) : null;
      const shipping = order?.ShippingEstimateHandlingLimitDate ? startOfDay(order.ShippingEstimateHandlingLimitDate) : null;
      if (!delivery || !shipping) return false;
      return delivery.getTime() < shipping.getTime();
    }

    function findRow(cfg) {
      return baseSuppliers.find(
        (sup) => sup.name === cfg.name && sup.time_collection === cfg.time_collection
      );
    }

    function registerOrderInSupplier(order, targetConfig, counterField, dataField) {
      const supplierRow = findRow(targetConfig);
      if (!supplierRow) return null;
      supplierRow[counterField] += 1;
      supplierRow.orders += 1;

      if (isExpressSupplier(supplierRow)) {
        // Marca TODO pedido da expressa, em qualquer coluna. É o que o modal
        // lê pra mostrar HOJE na coleta — inclusive em ENVIADO e nos ENVIOS,
        // que não têm faixa de urgência e ficavam exibindo o horário de
        // coleta que a expressa não tem.
        order._expressCarrier = true;

        // A faixa de urgência só existe nas quatro colunas operacionais: é
        // ela que o modal usa pra destacar atrasado e "faltam 30m".
        if (supplierRow.expressByStatus[counterField]) {
          const urgency = classifyExpressOrder(order, nowMs);
          order._expressUrgency = urgency;
          supplierRow.expressByStatus[counterField][urgency] += 1;
        }
      }

      supplierRow[dataField].push(order);
      return supplierRow;
    }

    // Marca o pedido como atrasado. `counterField` é a coluna de status onde
    // ele aparece — sem ela (ex.: AG. ETIQUETA, que não tem coluna própria) o
    // pedido só entra no total de ATRASADOS.
    function registerDelayedOrder(supplierRow, order, counterField) {
      supplierRow.ordersDelayed += 1;
      supplierRow.dataOrdersDelayed.push(order);
      if (counterField) {
        supplierRow.delayedByColumn[counterField] =
          (supplierRow.delayedByColumn[counterField] || 0) + 1;
      }
    }

    // Pendentes (fechados / separação / embalagem / embarque):
    //   embarque <= hoje  → coluna de status (e ATRASADO se < hoje)
    //   embarque == amanhã (próx. dia útil) → previsão amanhã
    //   embarque > amanhã → previsão +48h (informativo)
    function processPendingOrders(orderList, counterField, dataField, statusLabel) {
      if (!orderList?.data?.length) return;

      orderList.data.forEach((order) => {
        // Anota o status operacional pra usar nos modais (coluna + agrupamento)
        order._categoryStatus = statusLabel;

        const limitDate = getLimitDate(order);
        if (!limitDate) {
          // Sem data de embarque: não dá pra alocar em coleta nenhuma. Entra no
          // alerta pra recalcular (só os que ainda não foram enviados).
          // No modo filtrado o pedido não pertence a dia nenhum — fica de fora.
          if (!filterDay && !order.ShippingDate) noShipDateOrders.push(order);
          return;
        }

        const cfg = getConfigForLimit(order, limitDate, suppliersData.data);
        if (!cfg) return;

        const limitDay = startOfDay(limitDate).getTime();

        if (filterDay) {
          // Dia filtrado: sem previsão (amanhã / +48h) — todo pendente vai pra
          // coluna do seu status, e atrasa quem tem expedição anterior a hoje.
          const row = registerOrderInSupplier(order, cfg, counterField, dataField);
          if (row && limitDay < today.getTime()) {
            registerDelayedOrder(row, order, counterField);
          }
        } else if (limitDay <= today.getTime()) {
          const row = registerOrderInSupplier(order, cfg, counterField, dataField);
          if (row && limitDay < today.getTime()) {
            registerDelayedOrder(row, order, counterField);
          }
        } else if (limitDay === nbd.getTime()) {
          const row = findRow(cfg);
          if (row) {
            row.ordersNextDay += 1;
            row.dataOrdersNextDay.push(order);
          }
        } else {
          forecast.next48h += 1;
          forecast.dataNext48h.push(order);
        }

        // Alerta: entrega antes do embarque (só pra NÃO marketplaces).
        // Status 23 (aguardando etiqueta = marketplaces) é processado
        // em outra função — aqui todos são "não marketplace".
        // Skip pedidos já ENVIADOS (têm ShippingDate preenchida) — só
        // pedidos pendentes de embarque interessam pro alerta operacional.
        if (isDeliveryBeforeShipping(order) && !order.ShippingDate) {
          deliveryAlertOrders.push(order);
        }
      });
    }

    // Em espera de etiqueta (status 23 = marketplaces): não vai pro alerta
    function processAwaitingLabel(orderList) {
      if (!orderList?.data?.length) return;

      orderList.data.forEach((order) => {
        // Anota status pra modal — marketplace tem categoria própria
        order._categoryStatus = "AG. ETIQUETA";

        const limitDate = getLimitDate(order);
        if (!limitDate) {
          // Sem data de embarque → alerta pra recalcular (vale pra marketplace também)
          if (!filterDay && !order.ShippingDate) noShipDateOrders.push(order);
          return;
        }

        const cfg = getConfigForLimit(order, limitDate, suppliersData.data);
        if (!cfg) return;

        const row = findRow(cfg);
        if (!row) return;

        const limitDay = startOfDay(limitDate).getTime();

        if (filterDay) {
          // Marketplace: atrasa quando a expedição dele já passou de hoje
          if (limitDay < today.getTime()) registerDelayedOrder(row, order, null);
        } else if (limitDay <= today.getTime()) {
          registerDelayedOrder(row, order, null);
        } else if (limitDay === nbd.getTime()) {
          row.ordersNextDay += 1;
          row.dataOrdersNextDay.push(order);
        } else {
          forecast.next48h += 1;
          forecast.dataNext48h.push(order);
        }
      });
    }

    // Enviados hoje: janela de coleta pela HORA DE ENVIO; prazo x data de embarque
    function processShippedOrders(orderList) {
      if (!orderList?.data?.length) return;

      orderList.data.forEach((order) => {
        const cfg = getShippedCutoffConfig(order, suppliersData.data);
        if (!cfg) return;

        const row = registerOrderInSupplier(order, cfg, "ordersShipped", "dataOrdersShipped");
        if (!row) return;

        // A linha já diz se a transportadora conta prazo em horas.
        const timing = getShipmentTiming(
          order.ShippingDate,
          getLimitDate(order),
          isExpressSupplier(row)
        );
        if (timing === "early") {
          row.ordersEarlyShipping += 1;
          row.dataOrdersEarlyShipping.push(order);
        } else if (timing === "on_time") {
          row.ordersOnTimeShipping += 1;
          row.dataOrdersOnTimeShipping.push(order);
        } else if (timing === "late") {
          row.ordersLateShipping += 1;
          row.dataOrdersLateShipping.push(order);
        }
      });
    }

    // Expedição era o dia filtrado e o pedido saiu DEPOIS. Não entra em
    // "envio com atraso" (no dia filtrado ele não saiu) nem em coluna de
    // status (já não está pendente) — só engrossa EXPEDIR NO DIA e o TOTAL.
    function processShippedAfterDateOrders(orderList) {
      if (!orderList?.data?.length) return;

      orderList.data.forEach((order) => {
        order._categoryStatus = "EXPEDIR NO DIA";

        const limitDate = getLimitDate(order);
        if (!limitDate) return;

        const cfg = getConfigForLimit(order, limitDate, suppliersData.data);
        if (!cfg) return;

        registerOrderInSupplier(
          order,
          cfg,
          "ordersShippedAfterDate",
          "dataOrdersShippedAfterDate"
        );
      });
    }

    processPendingOrders(ordersClosed, "ordersClosed", "dataOrdersClosed", "AG. INÍCIO");
    processPendingOrders(ordersInPicking, "ordersInPicking", "dataOrdersInPicking", "EM SEPARAÇÃO");
    processPendingOrders(ordersAwaitingManifest, "ordersAwaitingManifest", "dataOrdersAwaitingManifest", "AG. EMBARQUE");
    processPendingOrders(ordersAwaitingPackaging, "ordersAwaitingPackaging", "dataOrdersAwaitingPackaging", "AG. EMBALAGEM");
    processAwaitingLabel(ordersAwaitingLabel);
    processShippedOrders(ordersShipped);
    processShippedAfterDateOrders(ordersShippedAfterDate);

    const sortedSuppliers = baseSuppliers.sort((a, b) => {
      // Expressas sempre no topo. A ordenação por hora de coleta não diz nada
      // sobre elas — o prazo é contado em horas a partir da criação, e o que
      // vence em 30 minutos não pode estar no meio da lista.
      const expressA = isExpressSupplier(a);
      const expressB = isExpressSupplier(b);
      if (expressA !== expressB) return expressA ? -1 : 1;

      if (a.time_collection !== b.time_collection) {
        return a.time_collection - b.time_collection;
      }
      return a.name.localeCompare(b.name);
    });

    setSuppliers(sortedSuppliers);

    // Agrega todos os pedidos de "próximo dia" espalhados nos suppliers
    const dataOrdersNextDayAll = sortedSuppliers.flatMap((i) => i.dataOrdersNextDay || []);

    // Todos os pedidos que compoem a coluna TOTAL da tabela (mesma soma do
    // rodape): pendentes por etapa + enviados. Usado no modal do TOTAL.
    const TOTAL_DATA_FIELDS = [
      "dataOrdersClosed",
      "dataOrdersInPicking",
      "dataOrdersAwaitingPackaging",
      "dataOrdersAwaitingManifest",
      "dataOrdersShipped",
      "dataOrdersShippedAfterDate",
    ];
    const allOrdersInTable = sortedSuppliers.flatMap((supplierRow) =>
      TOTAL_DATA_FIELDS.flatMap((dataField) => supplierRow[dataField] || [])
    );

    // Resumo por PORTE do pedido: quantos pedidos e quantas pecas cada porte
    // representa (a media de pecas sai da divisao). Transportadora sem
    // order_size cadastrado nao entra em nenhum porte, so no total geral.
    // "shippedOrders" (coluna FEITO) = o que já foi enviado no dia, ou seja
    // apenas o recorte de dataOrdersShipped dentro do total do porte.
    const ordersBySize = {};
    ORDER_SIZE_SUMMARY.forEach(({ key }) => {
      ordersBySize[key] = { orders: 0, items: 0, shippedOrders: 0 };
    });
    sortedSuppliers.forEach((supplierRow) => {
      const sizeBucket = ordersBySize[supplierRow.order_size];
      if (!sizeBucket) return;
      TOTAL_DATA_FIELDS.forEach((dataField) => {
        (supplierRow[dataField] || []).forEach((order) => {
          sizeBucket.orders += 1;
          sizeBucket.items += getOrderItemCount(order);
          if (dataField === "dataOrdersShipped") sizeBucket.shippedOrders += 1;
        });
      });
    });
    const totalItemsInTable = allOrdersInTable.reduce(
      (accumulated, order) => accumulated + getOrderItemCount(order),
      0
    );

    // Atrasados por coluna, somados de todas as transportadoras — usado no
    // rodapé da tabela, pro "(n)" bater com a soma das linhas acima.
    const delayedByColumnTotals = {};
    sortedSuppliers.forEach((supplierRow) => {
      Object.keys(supplierRow.delayedByColumn).forEach((counterField) => {
        delayedByColumnTotals[counterField] =
          (delayedByColumnTotals[counterField] || 0) +
          supplierRow.delayedByColumn[counterField];
      });
    });

    setTotals({
      // O rótulo da previsão precisa do MESMO dia que bucketizou os pedidos.
      // Recalcular na renderização daria divergência entre o texto e a conta.
      forecastDay: nbd,
      orders: sortedSuppliers.reduce((acc, i) => acc + i.orders, 0),
      ordersAwaitingManifest: sortedSuppliers.reduce((acc, i) => acc + i.ordersAwaitingManifest, 0),
      ordersAwaitingPackaging: sortedSuppliers.reduce((acc, i) => acc + i.ordersAwaitingPackaging, 0),
      ordersInPicking: sortedSuppliers.reduce((acc, i) => acc + i.ordersInPicking, 0),
      ordersClosed: sortedSuppliers.reduce((acc, i) => acc + i.ordersClosed, 0),
      ordersShipped: sortedSuppliers.reduce((acc, i) => acc + i.ordersShipped, 0),
      ordersEarlyShipping: sortedSuppliers.reduce((acc, i) => acc + i.ordersEarlyShipping, 0),
      ordersLateShipping: sortedSuppliers.reduce((acc, i) => acc + i.ordersLateShipping, 0),
      ordersOnTimeShipping: sortedSuppliers.reduce((acc, i) => acc + i.ordersOnTimeShipping, 0),
      ordersDelayed: sortedSuppliers.reduce((acc, i) => acc + i.ordersDelayed, 0),
      ordersNextDay: sortedSuppliers.reduce((acc, i) => acc + i.ordersNextDay, 0),
      ordersShippedAfterDate: sortedSuppliers.reduce((acc, i) => acc + i.ordersShippedAfterDate, 0),
      dataOrdersShippedAfterDate: sortedSuppliers.flatMap((i) => i.dataOrdersShippedAfterDate || []),
      ordersNext48h: forecast.next48h,
      // Arrays completos pros modais clicáveis (agregados de todos os suppliers)
      dataOrdersNextDay: dataOrdersNextDayAll,
      dataOrdersNext48h: forecast.dataNext48h,
      // Alerta: entrega antes do embarque (só não-marketplaces)
      ordersDeliveryAlert: deliveryAlertOrders.length,
      dataOrdersDeliveryAlert: deliveryAlertOrders,
      // Alerta: backend não calculou a data de embarque → precisa recalcular
      ordersNoShipDate: noShipDateOrders.length,
      dataOrdersNoShipDate: noShipDateOrders,
      // Todos os pedidos da tabela (modal do TOTAL)
      dataOrdersAll: allOrdersInTable,
      // Resumo por porte do pedido (rodape do painel)
      ordersBySize,
      totalItems: totalItemsInTable,
      // Atrasados por coluna de status (rodape da tabela)
      delayedByColumn: delayedByColumnTotals,
    });
  }, [
    suppliersData,
    ordersAwaitingManifest,
    ordersAwaitingPackaging,
    ordersInPicking,
    ordersClosed,
    ordersShipped,
    ordersAwaitingLabel,
    ordersShippedAfterDate,
    appliedFilterDate,
  ]);

  function getDataModal(name, time_collection, status, data) {
    const arrayData = suppliers.filter(
      (s) => s.time_collection === time_collection && s.name === name
    );

    if (!arrayData.length) return;

    setDataModal({
      name,
      time_collection,
      status,
      data: arrayData[0][data],
      tabs: null,
    });
    setModalSearch("");
    setModalStatusFilter("");
    setOverviewTab("day");
    setListView("list");
    setModal(true);
  }

  // Valor da célula com a quantidade de atrasados entre parênteses, quando
  // houver. Ex.: "30 (28)" — 30 na etapa, 28 deles já com o embarque vencido.
  function cellWithDelayed(value, delayedCount) {
    if (!delayedCount) return value;
    return (
      <>
        {value} <em className="delayedCount">({delayedCount})</em>
      </>
    );
  }

  // Pedido atrasado = data de embarque anterior a hoje. Mesmo critério da
  // coluna ATRASADOS, pra listagem e tabela contarem a mesma coisa.
  function isOrderDelayed(order) {
    const limitDate = getLimitDate(order);
    if (!limitDate) return false;
    const limitDay = startOfDay(limitDate);
    const todayStart = startOfDay(new Date());
    return !!limitDay && limitDay.getTime() < todayStart.getTime();
  }

  // Ordena pedidos por data de embarque (getLimitDate) crescente.
  // Null/inválido vai pro fim.
  function sortByShippingDate(list) {
    return [...(list || [])].sort((a, b) => {
      const da = getLimitDate(a);
      const db = getLimitDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.getTime() - db.getTime();
    });
  }

  // Recalcula a data de embarque de UM pedido no backend
  async function recalcOrder(order) {
    const id = order?.IDOrder;
    if (!id) throw new Error("IDOrder não informado");
    return apiLabs.post(`embarque/calcular/${id}`);
  }

  // Handler individual — botão "Recalcular" da linha
  async function handleRecalcOne(order) {
    if (!canRecalcPermission) return;
    if (recalcRow || recalcBulk.active) return;
    setRecalcRow(order.IDOrder);
    try {
      await recalcOrder(order);
      // Refresh do dashboard pra puxar a data recalculada
      setRefreshTrigger((x) => x + 1);
    } catch (err) {
      console.error("Recalc failed:", err);
    } finally {
      setRecalcRow(null);
    }
  }

  // Handler em massa — botão "Recalcular Todos"
  // Executa 1 por vez, com intervalo de 1s entre chamadas
  async function handleRecalcAll(list) {
    if (!canRecalcPermission) return;
    if (recalcBulk.active) return;
    const targets = list || [];
    if (!targets.length) return;
    setRecalcBulk({ active: true, current: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      setRecalcBulk({ active: true, current: i + 1, total: targets.length });
      try {
        // eslint-disable-next-line no-await-in-loop
        await recalcOrder(targets[i]);
      } catch (err) {
        console.warn("Recalc falhou pra IDOrder", targets[i].IDOrder, err?.message);
      }
      if (i < targets.length - 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
    setRecalcBulk({ active: false, current: 0, total: 0 });
    // Refresh final pra puxar os dados atualizados
    setRefreshTrigger((x) => x + 1);
  }


  // Abre modal genérico com uma lista de pedidos (usado por total forecast e alerta)
  // `allowAnalytics` liga a Visão Analítica — hoje só no modal do TOTAL, que é
  // o único conjunto que representa a tabela inteira e vale analisar.
  function openListModal(status, list, allowAnalytics = false) {
    setDataModal({
      name: "",
      time_collection: 0,
      status,
      data: sortByShippingDate(list),
      tabs: null,
      allowAnalytics,
    });
    setModalSearch("");
    setModalStatusFilter("");
    setOverviewTab("day");
    setListView("list");
    setModal(true);
  }

  // Abre modal com abas — pode misturar tabs de "overview" (agregado) e "list" (pedidos).
  // Cada tab: { label, kind: 'overview' | 'list', data: [pedidos], sources?: {label: []} }
  // sources.pivotSources é usado no overview pra permitir separar por origem (nextDay/next48h).
  function openTabsModal(status, tabs) {
    const preparedTabs = tabs.map((t) => ({
      ...t,
      kind: t.kind || "list",
      data: t.kind === "overview" ? (t.data || []) : sortByShippingDate(t.data),
    }));
    setDataModal({
      name: "",
      time_collection: 0,
      status,
      data: preparedTabs[0]?.data || [],
      tabs: preparedTabs,
      activeTab: 0,
    });
    setModalSearch("");
    setModalStatusFilter("");
    setOverviewTab("day");
    setListView("list");
    setModal(true);
  }

  // ── HELPERS PIVOT ─────────────────────────────────────────────────────────
  // Hora da ÚLTIMA coleta do supplier do pedido (transportadoras com múltiplas
  // coletas → considera a mais tarde do dia).
  function lastCollectionDecimal(order) {
    const configs = getSupplierConfigsByOrder(order, suppliersData.data);
    if (!configs.length) return null;
    return configs[configs.length - 1].time_collection; // ordenados asc
  }

  // Formata Date direto como "DD/MM SEG" (sem depender de order)
  function formatDayLabel(d) {
    if (!d) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const weekday = ["DOM", "SEG", "TER", "QUA", "QUI", "SEX", "SÁB"][d.getDay()];
    return `${dd}/${mm} ${weekday}`;
  }

  // Chave da coluna (data + dia da semana): "DD/MM SEG"
  function dayKey(order) {
    return formatDayLabel(getLimitDate(order));
  }

  // Chave ordenável (timestamp) usada pra sort de colunas
  function daySortKey(order) {
    const d = getLimitDate(order);
    if (!d) return Number.MAX_SAFE_INTEGER;
    return startOfDay(d).getTime();
  }

  // Retorna N dias úteis a partir de startDate (inclusive), pulando sáb/dom/feriados.
  function getBusinessDaysWindow(startDate, count) {
    const days = [];
    const d = startOfDay(startDate);
    while (days.length < count) {
      if (!isBlockedDate(d)) days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return days;
  }

  // Monta uma tabela pivot: linhas × colunas × contagem
  // rows: { key, label, sortValue } — linhas
  // cols: { key, label, sortValue } — colunas
  // cell counts em cellMap[rowKey][colKey]
  // opts.fixedCols: array de colunas pré-definidas — todas aparecem mesmo sem dado
  // Nome ABREVIADO da transportadora (campo `name` no config do supplier).
  function getCarrierShortName(order) {
    const configs = getSupplierConfigsByOrder(order, suppliersData.data);
    if (configs.length && configs[0].name) return configs[0].name;
    return order?.SupplierNameCorporateName || "SEM TRANSPORTADORA";
  }

  // Porte do pedido = order_size da transportadora que o leva. Sem porte
  // cadastrado vira uma linha própria, pras somas continuarem fechando.
  const ORDER_SIZE_ROW = {};
  ORDER_SIZE_SUMMARY.forEach(({ key, label }, index) => {
    ORDER_SIZE_ROW[key] = { label, sortValue: index };
  });

  function getOrderSizeRow(order) {
    const configs = getSupplierConfigsByOrder(order, suppliersData.data);
    const sizeKey = configs.length ? configs[0].order_size : null;
    const known = sizeKey ? ORDER_SIZE_ROW[sizeKey] : null;
    if (known) {
      return { key: `z:${sizeKey}`, label: known.label, sortValue: known.sortValue };
    }
    return { key: "z:sem", label: "SEM PORTE", sortValue: 99 };
  }

  /**
   * Agrega o conjunto de pedidos do modal pra Visão Analítica.
   *
   * Cinco baldes de prazo, mutuamente exclusivos — os três primeiros só pra
   * quem já saiu, comparando envio × expedição:
   *   no prazo / antecipado / enviado com atraso / atrasado (pendente e
   *   vencido) / a vencer (pendente e ainda no prazo)
   */
  function buildAnalytics(orders) {
    const list = orders || [];
    const todayStart = startOfDay(new Date());

    const emptyBucket = () => ({ orders: 0, items: 0, value: 0 });
    const analytics = {
      totalOrders: list.length,
      totalItems: 0,
      totalValue: 0,
      shipped: emptyBucket(),
      pending: emptyBucket(),
      noShipDate: 0,
      deliveryRisk: 0,
      timing: {
        on_time: emptyBucket(),
        early: emptyBucket(),
        late: emptyBucket(),
        overdue: emptyBucket(),
        upcoming: emptyBucket(),
      },
      byStatus: new Map(),
      bySize: new Map(),
      byCarrier: new Map(),
    };

    const addTo = (bucket, items, value) => {
      bucket.orders += 1;
      bucket.items += items;
      bucket.value += value;
    };
    const addToMap = (map, key, extra) => {
      if (!map.has(key)) map.set(key, { ...emptyBucket(), key, delayed: 0 });
      const bucket = map.get(key);
      bucket.orders += 1;
      bucket.items += extra.items;
      bucket.value += extra.value;
      if (extra.isDelayed) bucket.delayed += 1;
      return bucket;
    };

    list.forEach((order) => {
      const items = getOrderItemCount(order);
      const value = getOrderValue(order);
      const limitDate = getLimitDate(order);
      const isShipped = !!order.ShippingDate;

      analytics.totalItems += items;
      analytics.totalValue += value;
      if (!limitDate) analytics.noShipDate += 1;
      // Risco: a entrega prometida é ANTERIOR à data de embarque — prazo
      // apertado, mesmo alerta do header. Só vale pra quem ainda não saiu.
      if (!isShipped && order.ShippingEstimateDate && limitDate) {
        const deliveryDay = startOfDay(order.ShippingEstimateDate);
        const limitDay = startOfDay(limitDate);
        if (deliveryDay && limitDay && deliveryDay.getTime() < limitDay.getTime()) {
          analytics.deliveryRisk += 1;
        }
      }

      addTo(isShipped ? analytics.shipped : analytics.pending, items, value);

      let timingKey;
      if (isShipped) {
        // Aqui não existe a linha da transportadora — a visão analítica roda
        // sobre uma lista solta de pedidos. O modo sai do cadastro, pelo
        // supplier_name do próprio pedido.
        const configs = getSupplierConfigsByOrder(order, suppliersData.data);
        timingKey =
          getShipmentTiming(order.ShippingDate, limitDate, isExpressSupplier(configs[0])) ||
          "on_time";
      } else {
        const limitDay = limitDate ? startOfDay(limitDate) : null;
        timingKey =
          limitDay && limitDay.getTime() < todayStart.getTime() ? "overdue" : "upcoming";
      }
      const isDelayed = timingKey === "overdue";
      addTo(analytics.timing[timingKey], items, value);

      addToMap(analytics.byStatus, order._categoryStatus || "ENVIADO", { items, value, isDelayed });
      addToMap(analytics.bySize, getOrderSizeRow(order).label, { items, value, isDelayed });
      addToMap(analytics.byCarrier, getCarrierShortName(order), { items, value, isDelayed });
    });

    const sortedByOrders = (map) =>
      [...map.values()].sort((a, b) => b.orders - a.orders || a.key.localeCompare(b.key));

    analytics.statusRows = sortedByOrders(analytics.byStatus);
    analytics.carrierRows = sortedByOrders(analytics.byCarrier);
    // Porte segue a ordem do painel da tela (GRANDE → MÉDIO → PEQUENO)
    const sizeOrder = ORDER_SIZE_SUMMARY.map((s) => s.label);
    analytics.sizeRows = [...analytics.bySize.values()].sort((a, b) => {
      const ia = sizeOrder.indexOf(a.key);
      const ib = sizeOrder.indexOf(b.key);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });

    analytics.shippedPercentage = analytics.totalOrders
      ? (analytics.shipped.orders / analytics.totalOrders) * 100
      : 0;
    analytics.averageTicket = analytics.totalOrders
      ? analytics.totalValue / analytics.totalOrders
      : 0;
    analytics.averageItems = analytics.totalOrders
      ? analytics.totalItems / analytics.totalOrders
      : 0;

    return analytics;
  }

  // ── VISÃO ANALÍTICA ────────────────────────────────────────────────────────
  const TIMING_BLOCKS = [
    { key: "on_time", label: "NO PRAZO", tone: "ok" },
    { key: "early", label: "ANTECIPADO", tone: "warn" },
    { key: "late", label: "ENVIADO C/ ATRASO", tone: "danger" },
    { key: "overdue", label: "ATRASADO (NÃO SAIU)", tone: "danger" },
    { key: "upcoming", label: "A VENCER", tone: "neutral" },
  ];

  function renderAnalytics(analytics) {
    if (!analytics.totalOrders) {
      return <div className={styles.modalEmptyBox}>Nenhum pedido pra analisar.</div>;
    }

    const percentageOf = (part) =>
      analytics.totalOrders ? (part / analytics.totalOrders) * 100 : 0;

    // Barra proporcional dentro das tabelas — leitura rápida de concentração
    const shareBar = (part) => (
      <span className={styles.shareBar}>
        <span style={{ width: `${Math.min(100, percentageOf(part))}%` }} />
      </span>
    );

    return (
      <div className={styles.analyticsWrap}>
        {/* Execução — o pulso do conjunto */}
        <div className={styles.analyticsKpis}>
          <div className="kpiBox">
            <span className="kpiLabel">Pedidos</span>
            <span className="kpiValue">{analytics.totalOrders}</span>
            <span className="kpiHint">{analytics.totalItems} peças</span>
          </div>
          <div className="kpiBox ok">
            <span className="kpiLabel">Embarcados</span>
            <span className="kpiValue">{analytics.shipped.orders}</span>
            <span className="kpiHint">
              {analytics.shippedPercentage.toFixed(1)}% · {analytics.shipped.items} peças
            </span>
          </div>
          <div className="kpiBox warn">
            <span className="kpiLabel">Pendentes de embarque</span>
            <span className="kpiValue">{analytics.pending.orders}</span>
            <span className="kpiHint">{analytics.pending.items} peças</span>
          </div>
          <div className="kpiBox danger">
            <span className="kpiLabel">Atrasados</span>
            <span className="kpiValue">{analytics.timing.overdue.orders}</span>
            <span className="kpiHint">embarque vencido e não saiu</span>
          </div>
          <div className="kpiBox">
            <span className="kpiLabel">Média de peças</span>
            <span className="kpiValue">{analytics.averageItems.toFixed(1)}</span>
            <span className="kpiHint">por pedido</span>
          </div>
        </div>

        {/* Financeiro — só pra quem pode ver valores */}
        {canSeeOrderValues && (
          <div className={styles.analyticsKpis}>
            <div className="kpiBox ok">
              <span className="kpiLabel">Valor embarcado</span>
              <span className="kpiValue money">{formatOrderValue(analytics.shipped.value)}</span>
              <span className="kpiHint">{analytics.shipped.orders} pedidos</span>
            </div>
            <div className="kpiBox warn">
              <span className="kpiLabel">Valor pendente de embarcar</span>
              <span className="kpiValue money">{formatOrderValue(analytics.pending.value)}</span>
              <span className="kpiHint">{analytics.pending.orders} pedidos</span>
            </div>
            <div className="kpiBox">
              <span className="kpiLabel">Valor total</span>
              <span className="kpiValue money">{formatOrderValue(analytics.totalValue)}</span>
              <span className="kpiHint">no conjunto exibido</span>
            </div>
            <div className="kpiBox">
              <span className="kpiLabel">Ticket médio</span>
              <span className="kpiValue money">{formatOrderValue(analytics.averageTicket)}</span>
              <span className="kpiHint">por pedido</span>
            </div>
          </div>
        )}

        {/* Alertas operacionais — só aparecem quando existem */}
        {(analytics.noShipDate > 0 || analytics.deliveryRisk > 0) && (
          <div className={styles.analyticsAlerts}>
            {analytics.noShipDate > 0 && (
              <span className="alertChip">
                ⚠ {analytics.noShipDate} sem data de embarque calculada
              </span>
            )}
            {analytics.deliveryRisk > 0 && (
              <span className="alertChip">
                ⚠ {analytics.deliveryRisk} com entrega prometida antes do embarque
              </span>
            )}
          </div>
        )}

        {/* Prazo: pedidos, peças e valor em cada balde */}
        <div className={styles.analyticsPanel}>
          <h4>Prazo de envio</h4>
          <div className={styles.timingGrid}>
            {TIMING_BLOCKS.map(({ key, label, tone }) => {
              const bucket = analytics.timing[key];
              return (
                <div key={key} className={`timingBox ${tone}`}>
                  <span className="timingLabel">{label}</span>
                  <span className="timingValue">{bucket.orders}</span>
                  <span className="timingHint">{bucket.items} peças</span>
                  {canSeeOrderValues && (
                    <span className="timingMoney">{formatOrderValue(bucket.value)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Status e porte lado a lado */}
        <div className={styles.analyticsRow}>
          <div className={styles.analyticsPanel}>
            <h4>Por status</h4>
            <table className={styles.analyticsTable}>
              <thead>
                <tr>
                  <th>Status</th><th className="num">Pedidos</th><th className="num">Peças</th>
                  {canSeeOrderValues && <th className="num">Valor</th>}
                </tr>
              </thead>
              <tbody>
                {analytics.statusRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.key}{shareBar(row.orders)}</td>
                    <td className="num">{row.orders}</td>
                    <td className="num">{row.items}</td>
                    {canSeeOrderValues && (
                      <td className="num">{formatOrderValue(row.value)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.analyticsPanel}>
            <h4>Por porte do pedido</h4>
            <table className={styles.analyticsTable}>
              <thead>
                <tr>
                  <th>Porte</th><th className="num">Pedidos</th><th className="num">Peças</th>
                  <th className="num">Média</th>
                  {canSeeOrderValues && <th className="num">Valor</th>}
                </tr>
              </thead>
              <tbody>
                {analytics.sizeRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.key}{shareBar(row.orders)}</td>
                    <td className="num">{row.orders}</td>
                    <td className="num">{row.items}</td>
                    <td className="num">
                      {row.orders ? (row.items / row.orders).toFixed(1) : '0.0'}
                    </td>
                    {canSeeOrderValues && (
                      <td className="num">{formatOrderValue(row.value)}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transportadoras — ordenadas por volume, com o atraso ao lado */}
        <div className={styles.analyticsPanel}>
          <h4>Por transportadora <span className="panelHint">ordenado por pedidos</span></h4>
          <table className={styles.analyticsTable}>
            <thead>
              <tr>
                <th>Transportadora</th>
                <th className="num">Pedidos</th>
                <th className="num">Peças</th>
                <th className="num">Atrasados</th>
                {canSeeOrderValues && <th className="num">Valor</th>}
              </tr>
            </thead>
            <tbody>
              {analytics.carrierRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.key}{shareBar(row.orders)}</td>
                  <td className="num">{row.orders}</td>
                  <td className="num">{row.items}</td>
                  <td className={`num ${row.delayed > 0 ? 'danger' : ''}`}>
                    {row.delayed || '—'}
                  </td>
                  {canSeeOrderValues && (
                    <td className="num">{formatOrderValue(row.value)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function buildPivot(orders, getRow, getCol, opts) {
    const rowMap = new Map(); // key → { label, sortValue }
    const colMap = new Map();
    const cellMap = {};       // rowKey → { colKey → count }
    let grand = 0;

    // Medida secundária opcional (peças ou valor), somada em cada célula, por
    // linha, por coluna e no geral. Aparece entre parênteses junto da contagem.
    const getWeight = opts && opts.getWeight;
    const cellWeightMap = {}; // rowKey → { colKey → soma }
    const rowWeights = {};
    const colWeights = {};
    let grandWeight = 0;

    // Pré-popula colunas fixas — garante que todas apareçam mesmo sem pedidos
    if (opts && Array.isArray(opts.fixedCols)) {
      opts.fixedCols.forEach((c) => colMap.set(c.key, c));
    }

    (orders || []).forEach((o) => {
      const row = getRow(o);
      const col = getCol(o);
      if (!row || !col) return;
      if (!rowMap.has(row.key)) rowMap.set(row.key, row);
      if (!colMap.has(col.key)) colMap.set(col.key, col);
      if (!cellMap[row.key]) cellMap[row.key] = {};
      cellMap[row.key][col.key] = (cellMap[row.key][col.key] || 0) + 1;
      grand += 1;

      if (getWeight) {
        const weight = getWeight(o) || 0;
        if (!cellWeightMap[row.key]) cellWeightMap[row.key] = {};
        cellWeightMap[row.key][col.key] =
          (cellWeightMap[row.key][col.key] || 0) + weight;
        rowWeights[row.key] = (rowWeights[row.key] || 0) + weight;
        colWeights[col.key] = (colWeights[col.key] || 0) + weight;
        grandWeight += weight;
      }
    });

    const sortByValue = (a, b) => (a.sortValue - b.sortValue) || String(a.label).localeCompare(String(b.label));
    const rows = Array.from(rowMap.values()).sort(sortByValue);
    const cols = Array.from(colMap.values()).sort(sortByValue);

    // Totais por linha e coluna
    const rowTotals = {};
    const colTotals = {};
    rows.forEach((r) => {
      let sum = 0;
      cols.forEach((c) => {
        const v = cellMap[r.key]?.[c.key] || 0;
        sum += v;
        colTotals[c.key] = (colTotals[c.key] || 0) + v;
      });
      rowTotals[r.key] = sum;
    });

    return {
      rows, cols, cellMap, rowTotals, colTotals, grand,
      cellWeightMap, rowWeights, colWeights, grandWeight,
      hasWeights: !!getWeight,
    };
  }

  function switchModalTab(idx) {
    setDataModal((m) => {
      if (!m.tabs || !m.tabs[idx]) return m;
      return { ...m, activeTab: idx, data: m.tabs[idx].data || [] };
    });
    setModalSearch("");
    setModalStatusFilter("");
  }

  function closeModal() {
    setModal(false);
    setDataModal({
      name: "",
      time_collection: 0,
      status: "",
      data: [],
      tabs: null,
      activeTab: 0,
    });
    setModalSearch("");
    setModalStatusFilter("");
    setOverviewTab("day");
    setListView("list");
  }

  function verifyHour(time_collection) {
    const now = new Date();
    const minutesNow = now.getHours() * 60 + now.getMinutes();
    const collectionMinutes = time_collection * 60;

    if (minutesNow >= collectionMinutes) {
      return "collectionFinish";
    }

    if (minutesNow >= collectionMinutes - 60) {
      return "collectionInTime";
    }

    return "collectionOk";
  }

  // Label da previsão. Sai do mesmo dia que bucketizou os pedidos (totals),
  // pra texto e número nunca discordarem.
  //
  // "AMANHÃ" quando é literalmente o dia seguinte; senão nomeia o dia. Sábado
  // é nomeado mesmo sendo amanhã: é a exceção nova, e na TV precisa ficar
  // explícito que aquela previsão é de sábado.
  const forecastDay = totals.forecastDay || nextBusinessDay(new Date());
  const forecastLabel = (() => {
    const tomorrow = startOfDay(new Date());
    tomorrow.setDate(tomorrow.getDate() + 1);
    const weekday = forecastDay.getDay();
    if (weekday !== 6 && forecastDay.getTime() === tomorrow.getTime()) {
      return "PREVISÃO AMANHÃ";
    }
    return `PREVISÃO ${WEEKDAY_NAMES[weekday]}`;
  })();

  // Próxima coleta: menor time_collection dos suppliers que ainda não passou.
  // Recalcula quando `now` muda (a cada segundo) — vira "—" quando não há mais.
  const nextCollectionHour = useMemo(() => {
    if (!suppliers.length) return null;
    const nowDecimal = now.getHours() + now.getMinutes() / 60;
    const uniqueTimes = Array.from(
      new Set(
        suppliers
          .map((s) => Number(s.time_collection))
          .filter((t) => Number.isFinite(t))
      )
    )
      .filter((t) => t > nowDecimal)
      .sort((a, b) => a - b);
    return uniqueTimes[0] ?? null;
  }, [suppliers, now]);

  return (
    <>
      <div className={styles.fullwidth}>
        <div className={styles.boxHead}>
          <h1>EXPEDIÇÃO</h1>
          <div className="kpi">
            <span className="label">
              {isDateFilterActive
                ? `EXPEDIR NO DIA ${formatInputDateLabel(appliedFilterDate)}`
                : "EXPEDIR HOJE"}
            </span>
            {/* No dia filtrado entram também os pedidos daquele dia que só
                saíram depois — não foram expedidos no dia, então pesam aqui. */}
            <span className="value">
              {totals.ordersAwaitingManifest +
                totals.ordersAwaitingPackaging +
                totals.ordersClosed +
                totals.ordersInPicking +
                (isDateFilterActive ? totals.ordersShippedAfterDate : 0)}
            </span>
            <span className="unit">pedidos</span>
          </div>
          <div className="kpi danger">
            <span className="label">ATRASADOS</span>
            <span className="value">{totals.ordersDelayed}</span>
            <span className="unit">pedidos</span>
          </div>

          {/* ALERTA: entrega < embarque (não marketplaces).
              Só aparece se houver pelo menos 1 pedido — evita ocupar espaço
              do header quando não há alerta pendente. */}
          {totals.ordersDeliveryAlert > 0 && (
            <div
              className="kpi alert"
              onClick={() => openListModal("ALERTA · ENTREGA ANTES DO EMBARQUE", totals.dataOrdersDeliveryAlert)}
              title="Pedidos cuja data de entrega é anterior à data limite de embarque"
            >
              <span className="label">⚠ ALERTA</span>
              <span className="value">{totals.ordersDeliveryAlert}</span>
              <span className="unit">pedidos</span>
            </div>
          )}

          {/* ALERTA: sem data de embarque calculada pelo backend.
              Esses pedidos não entram em nenhuma coleta — precisam de
              recálculo. O modal abre com "Recalcular" liberado em todos. */}
          {totals.ordersNoShipDate > 0 && (
            <div
              className="kpi alert"
              onClick={() => openListModal("ALERTA · SEM DATA DE EMBARQUE", totals.dataOrdersNoShipDate)}
              title="Pedidos sem data de embarque calculada — recalcule para que entrem na coleta"
            >
              <span className="label">⚠ SEM DATA EMBARQUE</span>
              <span className="value">{totals.ordersNoShipDate}</span>
              <span className="unit">pedidos</span>
            </div>
          )}

          {/* CENTRO — PRÓXIMA COLETA + DATA EMBARQUE lado a lado */}
          <div className="centerGroup">
            {/* DATA EMBARQUE — data grande + hora centralizada abaixo */}
            <div className="dateEmbarque">
              <span className="label">DATA EMBARQUE</span>
              <div className="boxDateEmbarque">
                <span className="date">
                  {isDateFilterActive
                    ? formatInputDateLabel(appliedFilterDate)
                    : now.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                </span>
                <span className="time">
                  {isDateFilterActive
                    ? 'FILTRADO'
                    : now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            {/* PRÓXIMA COLETA — versão original, piscando */}
            <div className="nextCollection">
              <span className="label">PRÓXIMA COLETA</span>
              <span className="value">
                {nextCollectionHour != null ? formatHourLabel(nextCollectionHour) : '—'}
              </span>
            </div>
          </div>

          {/* Previsão (direita) — clicável, abre modal com 3 abas (Visão Geral + Amanhã + +48h).
              No modo filtrado não faz sentido: previsão é sempre relativa a hoje. */}
          {!isDateFilterActive && (
          <div
            className="kpiForecast"
            onClick={() =>
              openTabsModal("PREVISÃO", [
                {
                  label: "VISÃO GERAL",
                  kind: "overview",
                  data: [...(totals.dataOrdersNextDay || []), ...(totals.dataOrdersNext48h || [])],
                  // Só a aba "Por Tipo e Valor" soma o dia de hoje — as demais
                  // continuam sendo previsão pura, de amanhã em diante.
                  todayData: totals.dataOrdersAll || [],
                },
                { label: forecastLabel, data: totals.dataOrdersNextDay },
                { label: "PREVISÃO +48H", data: totals.dataOrdersNext48h },
              ])
            }
            title="Clique para ver os pedidos da previsão"
          >
            <div className="fRow">
              <span className="label">{forecastLabel}</span>
              <span className="value">{totals.ordersNextDay}</span>
              <span className="unit">pedidos</span>
            </div>
            <div className="fRow sub">
              <span className="label">PREVISÃO +48H</span>
              <span className="value">{totals.ordersNext48h}</span>
              <span className="unit">pedidos</span>
            </div>
          </div>
          )}
        </div>


        {/* ── Layout de 2 tabelas lado a lado: principal + forecast ────────── */}
        <div className={styles.tablesRow}>

          <div className={styles.boxTable}>
            <div className={styles.tableHead}>
              <span className="nameTimeCollection">COLETA</span>
              <span className="nameTimeCollection">TRANSPORTADORA</span>
              <span>AG. INÍCIO</span>
              <span>EM SEPARAÇÃO</span>
              <span>AG. EMBALAGEM</span>
              <span>AG. EMBARQUE</span>
              <span>ENVIADO</span>
              <span className="colDelayed">ATRASADOS</span>
              <span>TOTAL</span>
              <span className="labelOnTimeShipping">ENVIOS NO PRAZO</span>
              <span className="labelEarlyShipping">ENVIOS ANTECIPADO</span>
              <span className="labelLateShipping">ENVIOS ATRASADO</span>
            </div>

            <div className={styles.tableValues}>
              {suppliers.map((s) => {
                const isExpress = isExpressSupplier(s);

                // Basta UMA das quatro etapas ter pedido vencendo em 30
                // minutos pra faixa inteira piscar: o aviso é da
                // transportadora, não da etapa.
                const hasExpressWarning =
                  isExpress &&
                  EXPRESS_COLUMNS.some(
                    (column) => (s.expressByStatus?.[column]?.faltam30 ?? 0) > 0
                  );

                // Conteúdo de uma coluna operacional. Na expressa vira a pilha
                // das três faixas, alinhada com os rótulos na coluna da
                // transportadora; nas demais, o número de sempre.
                const columnCell = (counterField, totalValue) => {
                  if (!isExpress) {
                    return cellWithDelayed(totalValue, s.delayedByColumn?.[counterField]);
                  }
                  const buckets = s.expressByStatus?.[counterField];
                  return (
                    <em className="expressStack">
                      {EXPRESS_STATUS_ROWS.map(({ key }) => {
                        const value = buckets?.[key] ?? 0;
                        // Zero não pinta: um fundo vermelho sobre "0" grita
                        // sem ter o que dizer, e com quatro colunas a linha
                        // ficaria vermelha o dia todo.
                        const alert = value > 0 && key === "atrasado";
                        return (
                          <em
                            key={key}
                            className={`expressCell ${key} ${alert ? "hasValue" : ""}`.trim()}
                          >
                            {value}
                          </em>
                        );
                      })}
                    </em>
                  );
                };

                // A expressa não tem janela de coleta, então não passa por
                // "coleta encerrada" — que apaga a linha. Ela opera o dia
                // inteiro e fica sempre ativa.
                const hourStatus = isExpress
                  ? "collectionOk"
                  : verifyHour(s.time_collection);
                // Classe do porte: pinta a borda lateral e o texto de
                // coleta/transportadora na cor cadastrada em order_size.
                const sizeClass = ORDER_SIZE_ROW_CLASS[s.order_size] || "";
                return (
                  <Row
                    key={`${s.name}-${s.time_collection}`}
                    status={hourStatus}
                    $expressWarning={hasExpressWarning}
                    className={`${hourStatus} ${sizeClass} ${isExpress ? "expressRow" : ""}`.trim()}
                  >
                    {/* Expressa não tem janela de coleta: o prazo conta em
                        horas a partir da criação, então o que vale é HOJE. */}
                    <span className="nameTimeCollection">
                      {isExpress ? "HOJE" : formatHourLabel(s.time_collection)}
                    </span>
                    <span className="nameTimeCollection">
                      {isExpress ? <em className="expressName">{s.name}</em> : s.name}
                      {isExpress && (
                        <em className="expressLabels">
                          {EXPRESS_STATUS_ROWS.map(({ key, label }) => (
                            <em key={key} className={`expressLabel ${key}`}>{label}</em>
                          ))}
                        </em>
                      )}
                    </span>
                    <span
                      className={
                        hourStatus === "collectionFinish" && s.ordersClosed > 0
                          ? "ordersRisk"
                          : ""
                      }
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "AG. INÍCIO",
                          "dataOrdersClosed"
                        )
                      }
                    >
                      {columnCell("ordersClosed", s.ordersClosed)}
                    </span>
                    <span
                      className={
                        hourStatus === "collectionFinish" && s.ordersInPicking > 0
                          ? "ordersRisk"
                          : ""
                      }
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "EM SEPARAÇÃO",
                          "dataOrdersInPicking"
                        )
                      }
                    >
                      {columnCell("ordersInPicking", s.ordersInPicking)}
                    </span>
                    <span
                      className={
                        hourStatus === "collectionFinish" &&
                          s.ordersAwaitingPackaging > 0
                          ? "ordersRisk"
                          : ""
                      }
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "AG. EMBALAGEM",
                          "dataOrdersAwaitingPackaging"
                        )
                      }
                    >
                      {columnCell("ordersAwaitingPackaging", s.ordersAwaitingPackaging)}
                    </span>
                    <span
                      className={
                        hourStatus === "collectionFinish" &&
                          s.ordersAwaitingManifest > 0
                          ? "ordersRisk"
                          : ""
                      }
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "AG. EMBARQUE",
                          "dataOrdersAwaitingManifest"
                        )
                      }
                    >
                      {columnCell("ordersAwaitingManifest", s.ordersAwaitingManifest)}
                    </span>
                    <span
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "ENVIADO",
                          "dataOrdersShipped"
                        )
                      }
                    >
                      {s.ordersShipped}
                    </span>
                    <span
                      className="colDelayed"
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "ATRASADOS",
                          "dataOrdersDelayed"
                        )
                      }
                    >
                      {s.ordersDelayed}
                    </span>
                    <span>{s.orders}</span>
                    <span
                      className="labelOnTimeShipping"
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "ENVIO NO PRAZO",
                          "dataOrdersOnTimeShipping"
                        )
                      }
                    >
                      {s.ordersOnTimeShipping}
                    </span>
                    <span
                      className="labelEarlyShipping"
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "ENVIO ANTECIPADO",
                          "dataOrdersEarlyShipping"
                        )
                      }
                    >
                      {s.ordersEarlyShipping}
                    </span>
                    <span
                      className="labelLateShipping"
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          "ENVIO ATRASADO",
                          "dataOrdersLateShipping"
                        )
                      }
                    >
                      {s.ordersLateShipping}
                    </span>
                  </Row>
                );
              })}

              <p className={styles.lineTotals}>
                <span>-</span>
                <span>-</span>
                <span>{cellWithDelayed(totals.ordersClosed, totals.delayedByColumn?.ordersClosed)}</span>
                <span>{cellWithDelayed(totals.ordersInPicking, totals.delayedByColumn?.ordersInPicking)}</span>
                <span>{cellWithDelayed(totals.ordersAwaitingPackaging, totals.delayedByColumn?.ordersAwaitingPackaging)}</span>
                <span>{cellWithDelayed(totals.ordersAwaitingManifest, totals.delayedByColumn?.ordersAwaitingManifest)}</span>
                <span>{totals.ordersShipped}</span>
                <span className="colDelayed">{totals.ordersDelayed}</span>
                {/* TOTAL clicável — abre o modal com TODOS os pedidos da tabela */}
                <span
                  className="labelTotal clickable"
                  onClick={() =>
                    openListModal("TODOS OS PEDIDOS", totals.dataOrdersAll, true)
                  }
                  title={`Ver todos os ${totals.orders} pedidos`}
                >
                  {totals.orders}
                </span>
                <span className="labelOnTimeShipping">{totals.ordersOnTimeShipping}</span>
                <span className="labelEarlyShipping">{totals.ordersEarlyShipping}</span>
                <span className="labelLateShipping">{totals.ordersLateShipping}</span>
              </p>
            </div>

          </div>

          {/* ── 2ª tabela isolada: PREVISÃO ──────────────────────────────────
              Escondida no modo filtrado — a previsão é relativa a hoje, e a
              tabela principal ocupa a largura toda (boxTable é flex:1). */}
          {!isDateFilterActive && (
          <div className={styles.boxTableForecast}>
            <div className={styles.tableHeadForecast}>
              <span>{forecastLabel}</span>
            </div>
            <div className={styles.tableValuesForecast}>
              {suppliers.map((s) => {
                // NÃO usa <Row> aqui — o styled Row aplica keyframes `pulse` na
                // linha da próxima coleta e essa animação NÃO deve vazar pra
                // tabela de previsão. Usamos <p> puro pra evitar o pulse.
                // A linha expressa é três vezes mais alta na tabela da
                // esquerda. Esta tabela é um elemento separado ao lado, então
                // sem a mesma classe as duas metades da mesma linha
                // desalinhariam daqui pra baixo.
                return (
                  <p
                    key={`fc-${s.name}-${s.time_collection}`}
                    className={isExpressSupplier(s) ? "expressRow" : undefined}
                  >
                    <span
                      onClick={() =>
                        getDataModal(
                          s.name,
                          s.time_collection,
                          forecastLabel,
                          "dataOrdersNextDay"
                        )
                      }
                    >
                      {s.ordersNextDay}
                    </span>
                  </p>
                );
              })}
              <p
                className={styles.lineTotalsForecast}
                onClick={() =>
                  totals.ordersNextDay > 0 &&
                  openListModal(forecastLabel, totals.dataOrdersNextDay)
                }
                title="Clique para ver todos os pedidos da previsão"
                style={{ cursor: totals.ordersNextDay > 0 ? "pointer" : "default" }}
              >
                <span>{totals.ordersNextDay}</span>
              </p>
            </div>
          </div>
          )}

        </div>
        {/* ── RESUMO POR PORTE DO PEDIDO ──────────────────────────────
            Classificação vem do campo order_size da transportadora.
            Transportadora sem porte cadastrado entra só no total geral. */}
        <div className={styles.sizeSummary}>
          {ORDER_SIZE_SUMMARY.map(({ key, label }) => {
            const sizeBucket =
              totals.ordersBySize?.[key] || { orders: 0, items: 0, shippedOrders: 0 };
            const averageItems =
              sizeBucket.orders > 0 ? sizeBucket.items / sizeBucket.orders : 0;
            return (
              <div key={key} className={`${styles.sizeCard} ${key}`}>
                <div className="cardTitle">{label}</div>
                <div className="cardMetrics">
                  <div className="metric">
                    <span className="metricLabel">TOTAL</span>
                    <span className="metricValue">{sizeBucket.orders}</span>
                  </div>
                  <div className="metric">
                    <span className="metricLabel">FEITO</span>
                    <span className="metricValue">{sizeBucket.shippedOrders}</span>
                  </div>
                  <div className="metric">
                    <span className="metricLabel">MÉDIA PÇ.</span>
                    <span className="metricValue">{averageItems.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Último bloco: fecha o painel com os totais gerais */}
          <div className={`${styles.sizeCard} total`}>
            <div className="cardTitle">TOTAL</div>
            <div className="cardMetrics">
              <div className="metric">
                <span className="metricLabel">TOTAL</span>
                <span className="metricValue">{totals.orders}</span>
              </div>
              <div className="metric">
                <span className="metricLabel">FEITO</span>
                <span className="metricValue">{totals.ordersShipped}</span>
              </div>
              <div className="metric">
                <span className="metricLabel">PEÇAS</span>
                <span className="metricValue">{totals.totalItems}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── FILTRO POR DATA ──────────────────────────────────────────────
            Escondido por padrão (tela de TV). Abre pelo botão do canto
            inferior direito. Com uma data aplicada, a tela inteira é remontada
            com os pedidos cuja expedição é aquele dia + os enviados no dia. */}
        {canUseDateFilter && showDateFilterPanel && (
          <div className={styles.dateFilterPanel}>
            <span className="title">FILTRAR POR DATA</span>

            <div className="controls">
              <input
                type="date"
                className="dateInput"
                value={filterDateInput}
                onChange={(event) => setFilterDateInput(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && applyDateFilter()}
                autoFocus
              />
              <button
                type="button"
                className="apply"
                onClick={applyDateFilter}
                disabled={!filterDateInput}
              >
                Filtrar
              </button>
              <button
                type="button"
                className="clear"
                onClick={clearDateFilter}
                disabled={!isDateFilterActive && !filterDateInput}
              >
                Limpar filtro
              </button>
            </div>

            {isDateFilterActive && (
              <span className="activeBadge">
                Exibindo expedição e envios de{" "}
                <strong>{formatInputDateLabel(appliedFilterDate)}</strong> ·
                pedidos enviados no dia não são contados de novo na expedição
              </span>
            )}
          </div>
        )}

        {/* Botão discreto: apaga sozinho quando não há filtro em uso.
            Visível só pra master e romulo. */}
        {canUseDateFilter && (
        <button
          type="button"
          className={`${styles.dateFilterToggle} ${isDateFilterActive ? "active" : ""}`}
          style={{ opacity: dimFilterButton ? 0.12 : 1 }}
          onMouseEnter={() => setDimFilterButton(false)}
          onClick={() => setShowDateFilterPanel((isOpen) => !isOpen)}
          title={
            isDateFilterActive
              ? `Tela filtrada em ${formatInputDateLabel(appliedFilterDate)} — clique para alterar ou limpar`
              : "Filtrar a tela por uma data de expedição/envio"
          }
        >
          {isDateFilterActive
            ? `📅 ${formatInputDateLabel(appliedFilterDate)}`
            : "📅 Filtrar por data"}
        </button>
        )}

      </div>

      {modal && (
        <div className={styles.modalContainer}>
          <div className={styles.modal}>
            <div className={styles.headModal}>
              <h1>Pedidos {dataModal.status}</h1>
              <span onClick={closeModal}>X</span>
            </div>

            {/* Tabs (só quando dataModal.tabs está setado) */}
            {dataModal.tabs && dataModal.tabs.length > 0 && (
              <div className={styles.modalTabs}>
                {dataModal.tabs.map((t, idx) => (
                  <button
                    key={`tab-${idx}`}
                    type="button"
                    className={idx === dataModal.activeTab ? "active" : ""}
                    onClick={() => switchModalTab(idx)}
                  >
                    {t.label} <span className="count">{(t.data || []).length}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Conteúdo do modal — muda conforme o tipo da tab ativa */}
            {(() => {
              const activeTabObj = dataModal.tabs ? dataModal.tabs[dataModal.activeTab] : null;
              const isOverview = activeTabObj?.kind === 'overview';

              // ── Visão Geral: 2 sub-abas (Por Dia / Por Status) ─────────────
              if (isOverview) {
                // Janela de 10 dias corridos a partir de hoje (inclusive).
                // Sábado, domingo e feriados que bloqueiam coleta são
                // desconsiderados dentro dessa janela — não aparecem como
                // colunas nem contam nos totais.
                const today0 = startOfDay(new Date());
                const windowEnd = new Date(today0);
                windowEnd.setDate(windowEnd.getDate() + 10);
                // "Por Tipo e Valor" abre com o dia de hoje junto; as outras
                // sub-abas continuam só com a previsão. Dedup por IDOrder é
                // defensivo: hoje e previsão são baldes mutuamente exclusivos.
                const overviewSource = (() => {
                  const forecastOrders = dataModal.data || [];
                  if (overviewTab !== 'value') return forecastOrders;
                  const byId = new Map();
                  [...forecastOrders, ...(activeTabObj.todayData || [])]
                    .forEach((o) => { if (!byId.has(o.IDOrder)) byId.set(o.IDOrder, o); });
                  return [...byId.values()];
                })();

                const orders = overviewSource.filter((o) => {
                  const d = getLimitDate(o);
                  if (!d) return false;
                  const t = startOfDay(d).getTime();
                  if (t < today0.getTime() || t >= windowEnd.getTime()) return false;
                  return !isBlockedDate(d);
                });

                // Ordem canônica pra "Por Status" — mesma da tabela principal
                const STATUS_ORDER = {
                  "AG. INÍCIO":     1,
                  "EM SEPARAÇÃO":   2,
                  "AG. EMBALAGEM":  3,
                  "AG. EMBARQUE":   4,
                  "AG. ETIQUETA":   5,
                };

                const carrierShortName = getCarrierShortName;
                const orderSizeRow = getOrderSizeRow;

                let pivot;
                if (overviewTab === 'day') {
                  pivot = buildPivot(
                    orders,
                    (o) => {
                      const t = lastCollectionDecimal(o);
                      if (t == null) return null;
                      return { key: `h:${t}`, label: formatHourLabel(t), sortValue: t };
                    },
                    (o) => ({ key: `d:${daySortKey(o)}`, label: dayKey(o), sortValue: daySortKey(o) }),
                  );
                } else if (overviewTab === 'status') {
                  pivot = buildPivot(
                    orders,
                    (o) => {
                      const s = o._categoryStatus || 'SEM STATUS';
                      return { key: `s:${s}`, label: s, sortValue: STATUS_ORDER[s] ?? 99 };
                    },
                    (o) => ({ key: `d:${daySortKey(o)}`, label: dayKey(o), sortValue: daySortKey(o) }),
                  );
                } else if (overviewTab === 'size' || overviewTab === 'value') {
                  // Porte do pedido (GRANDE / MÉDIO / PEQUENO) por dia, com uma
                  // segunda medida: peças na aba pública, valor na restrita.
                  pivot = buildPivot(
                    orders,
                    orderSizeRow,
                    (o) => ({ key: `d:${daySortKey(o)}`, label: dayKey(o), sortValue: daySortKey(o) }),
                    {
                      getWeight: overviewTab === 'value'
                        ? getOrderValue
                        : getOrderItemCount,
                    },
                  );
                } else {
                  // 'carrier' — linhas: nomes abreviados das transportadoras
                  pivot = buildPivot(
                    orders,
                    (o) => {
                      const name = carrierShortName(o);
                      return { key: `c:${name}`, label: name, sortValue: 0 };
                    },
                    (o) => ({ key: `d:${daySortKey(o)}`, label: dayKey(o), sortValue: daySortKey(o) }),
                  );
                }

                // Como a medida secundária aparece entre parênteses
                const formatPivotWeight = overviewTab === 'value'
                  ? formatOrderValue
                  : (value) => `${value} pç.`;

                // Peso da célula só faz sentido quando há pedido nela
                const renderPivotWeight = (value) =>
                  pivot.hasWeights ? (
                    <em className="pivotWeight">({formatPivotWeight(value || 0)})</em>
                  ) : null;

                return (
                  <>
                    {/* Sub-tabs Por Dia / Por Status / Por Transportadora */}
                    <div className={styles.modalSubTabs}>
                      <button
                        type="button"
                        className={overviewTab === 'day' ? 'active' : ''}
                        onClick={() => setOverviewTab('day')}
                      >
                        Por Dia
                      </button>
                      <button
                        type="button"
                        className={overviewTab === 'status' ? 'active' : ''}
                        onClick={() => setOverviewTab('status')}
                      >
                        Por Status
                      </button>
                      <button
                        type="button"
                        className={overviewTab === 'carrier' ? 'active' : ''}
                        onClick={() => setOverviewTab('carrier')}
                      >
                        Por Transportadora
                      </button>
                      <button
                        type="button"
                        className={overviewTab === 'size' ? 'active' : ''}
                        onClick={() => setOverviewTab('size')}
                      >
                        Por Tipo e Peças
                      </button>
                      {/* Valores: só master e romulo */}
                      {canSeeOrderValues && (
                        <button
                          type="button"
                          className={overviewTab === 'value' ? 'active' : ''}
                          onClick={() => setOverviewTab('value')}
                        >
                          Por Tipo e Valor
                        </button>
                      )}
                    </div>

                    {/* Pivot table */}
                    <div className={styles.pivotWrap}>
                      <table className={styles.pivotTable}>
                        <thead>
                          <tr>
                            <th className="rowLabel">
                              {overviewTab === 'day' ? 'HORÁRIO DE COLETA'
                                : overviewTab === 'status' ? 'STATUS'
                                : (overviewTab === 'size' || overviewTab === 'value') ? 'TIPO'
                                : 'TRANSPORTADORA'}
                            </th>
                            {pivot.cols.map((c) => (
                              <th key={c.key}>{c.label}</th>
                            ))}
                            <th className="totalCol">TOTAL GERAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pivot.rows.length === 0 ? (
                            <tr>
                              <td colSpan={pivot.cols.length + 2} className="empty">
                                Nenhum pedido pra agregar.
                              </td>
                            </tr>
                          ) : pivot.rows.map((r) => (
                            <tr key={r.key}>
                              <td className="rowLabel">{r.label}</td>
                              {pivot.cols.map((c) => {
                                const count = pivot.cellMap[r.key]?.[c.key] || 0;
                                return (
                                  <td key={c.key}>
                                    {count || ''}
                                    {count > 0 &&
                                      renderPivotWeight(pivot.cellWeightMap[r.key]?.[c.key])}
                                  </td>
                                );
                              })}
                              <td className="totalCol">
                                {pivot.rowTotals[r.key] || 0}
                                {renderPivotWeight(pivot.rowWeights[r.key])}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        {pivot.rows.length > 0 && (
                          <tfoot>
                            <tr>
                              <td className="rowLabel">TOTAL GERAL</td>
                              {pivot.cols.map((c) => (
                                <td key={c.key}>
                                  {pivot.colTotals[c.key] || 0}
                                  {renderPivotWeight(pivot.colWeights[c.key])}
                                </td>
                              ))}
                              <td className="totalCol">
                                {pivot.grand}
                                {renderPivotWeight(pivot.grandWeight)}
                              </td>
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </>
                );
              }

              // ── Lista de pedidos (busca + filtro de status + tabela) ────────
              const q = modalSearch.trim().toLowerCase();
              // Universo de status únicos no dataset atual — pra popular o filtro
              const statusOptions = Array.from(
                new Set((dataModal.data || []).map((o) => o._categoryStatus).filter(Boolean))
              ).sort();

              const filtered = (dataModal.data || []).filter((o) => {
                if (modalStatusFilter && o._categoryStatus !== modalStatusFilter) return false;
                if (!q) return true;
                return String(o.Order || '').toLowerCase().includes(q)
                  || String(o.IDOrder || '').toLowerCase().includes(q)
                  || String(o.SupplierNameCorporateName || '').toLowerCase().includes(q);
              });
              // Recálculo vale pra qualquer pedido da listagem — o lote segue
              // a busca e o filtro de status aplicados no modal.
              const recalcCandidates = filtered;

              // Alternador Lista / Analítica — a análise respeita a busca e o
              // filtro de status, então dá pra recortar e reanalisar.
              // Só no modal do TOTAL e só pra usuários-chave; nos demais nem o
              // alternador aparece (um botão "Lista" sozinho não serve pra nada).
              const analyticsEnabled = canSeeAnalytics && !!dataModal.allowAnalytics;
              const showAnalytics = analyticsEnabled && listView === 'analytics';
              const viewSwitch = analyticsEnabled ? (
                <div className={styles.modalViewSwitch}>
                  <button
                    type="button"
                    className={listView === 'list' ? 'active' : ''}
                    onClick={() => setListView('list')}
                  >
                    Lista
                  </button>
                  <button
                    type="button"
                    className={listView === 'analytics' ? 'active' : ''}
                    onClick={() => setListView('analytics')}
                  >
                    Visão Analítica
                  </button>
                </div>
              ) : null;

              if (showAnalytics) {
                return (
                  <>
                    {viewSwitch}
                    {renderAnalytics(buildAnalytics(filtered))}
                  </>
                );
              }

              return (
                <>
                  {viewSwitch}
                  <div className={styles.modalSearchWrap}>
                    <input
                      type="text"
                      className={styles.modalSearchInput}
                      placeholder="🔍 Buscar por número do pedido ou transportadora…"
                      value={modalSearch}
                      onChange={(e) => setModalSearch(e.target.value)}
                      autoFocus
                      disabled={recalcBulk.active}
                    />
                    {statusOptions.length > 0 && (
                      <select
                        className={styles.modalStatusFilter}
                        value={modalStatusFilter}
                        onChange={(e) => setModalStatusFilter(e.target.value)}
                        disabled={recalcBulk.active}
                      >
                        <option value="">Todos os status</option>
                        {statusOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    )}
                    <span className={styles.modalSearchCount}>
                      <strong>{filtered.length}</strong>
                      {filtered.length !== (dataModal.data || []).length && ` de ${(dataModal.data || []).length}`}
                      &nbsp;pedido{filtered.length !== 1 ? 's' : ''}
                    </span>
                    {canRecalcPermission && recalcCandidates.length > 0 && (
                      <button
                        type="button"
                        className={styles.recalcAllBtn}
                        disabled={recalcBulk.active || !!recalcRow}
                        onClick={() => handleRecalcAll(filtered)}
                        title={`Recalcular data de embarque de ${recalcCandidates.length} pedido(s)`}
                      >
                        {recalcBulk.active
                          ? `Recalculando ${recalcBulk.current}/${recalcBulk.total}…`
                          : `↻ Recalcular todos (${recalcCandidates.length})`}
                      </button>
                    )}
                  </div>

                  {recalcBulk.active && (
                    <div className={styles.recalcProgress}>
                      <div className={styles.recalcProgressText}>
                        Recalculando <strong>{recalcBulk.current}/{recalcBulk.total}</strong>
                      </div>
                      <div className={styles.recalcProgressBar}>
                        <div
                          className={styles.recalcProgressFill}
                          style={{ width: `${(recalcBulk.current / Math.max(1, recalcBulk.total)) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <div className={styles.tableModal}>
                    <div className="headTableModal">
                      <p>
                        <span>COLETA</span>
                        <span>STATUS</span>
                        <span>DATA CRIAÇÃO</span>
                        <span>DATA EMBALAGEM</span>
                        <span>DATA EMBARQUE</span>
                        <span>PREVISÃO ENTREGA</span>
                        <span>TRANSPORTADORA</span>
                        <span>PEDIDO</span>
                        <span>AÇÕES</span>
                      </p>
                    </div>
                    <div className="bodyTableModal">
                      {filtered.length === 0 ? (
                        <p className={styles.modalEmpty}>
                          <span>
                            {q || modalStatusFilter
                              ? `Nenhum pedido encontrado ${q ? `para "${modalSearch}"` : ''} ${modalStatusFilter ? `com status "${modalStatusFilter}"` : ''}`.trim()
                              : 'Nenhum pedido nesta lista'}
                          </span>
                        </p>
                      ) : filtered.map((order, index) => {
                        const showRecalc = canRecalcPermission;
                        const loadingThisRow = recalcRow === order.IDOrder;
                        return (
                          <p
                            key={`${order.IDOrder || order.Order}-${index}`}
                            // Na expressa o destaque vem da urgência marcada no
                            // ciclo (atrasado / faltam 30m). O delayedRow
                            // compara dias e nunca acusaria um prazo de horas.
                            className={
                              order._expressUrgency === "atrasado"
                                ? "expressLate"
                                : order._expressUrgency === "faltam30"
                                  ? "expressWarning"
                                  : isOrderDelayed(order)
                                    ? "delayedRow"
                                    : undefined
                            }
                          >
                            <span>
                              {/* Expressa não tem janela de coleta — mostrar
                                  o horário do cadastro aqui seria inventar um
                                  compromisso que não existe. A marca é por
                                  pedido porque o modal do TOTAL mistura
                                  transportadoras das duas modalidades. */}
                              {order._expressCarrier
                                ? 'HOJE'
                                : dataModal.time_collection
                                  ? formatHourLabel(dataModal.time_collection)
                                  : (order.ShippingEstimateHandlingLimitDate
                                    ? format(parseApiLocalDate(order.ShippingEstimateHandlingLimitDate), 'HH:mm')
                                    : ' - ')}
                            </span>
                            <span className="statusCell">{order._categoryStatus || '—'}</span>
                            <span>{order.Recordtimestamp ? format(parseApiLocalDate(order.Recordtimestamp), 'dd/MM/yyyy HH:mm') : " - "}</span>
                            {/* Só AG. EMBARQUE e enviados passam pelo Embalei,
                                então os outros ficam em "-" — ver fetchPackagingStatus. */}
                            <span>{order._packagedAt ? format(parseEmbaleiDate(order._packagedAt), 'dd/MM/yyyy HH:mm') : " - "}</span>
                            <span>{order.ShippingEstimateHandlingLimitDate ? format(parseApiLocalDate(order.ShippingEstimateHandlingLimitDate), 'dd/MM/yyyy HH:mm') : " - "}</span>
                            <span>{order.ShippingEstimateDate ? format(parseApiLocalDate(order.ShippingEstimateDate), 'dd/MM/yyyy') : " - "}</span>
                            <span className="SupplierNameCorporateName">{order.SupplierNameCorporateName}</span>
                            <span>{order.Order}</span>
                            <span>
                              {showRecalc && (
                                <button
                                  type="button"
                                  className={styles.recalcRowBtn}
                                  disabled={loadingThisRow || recalcBulk.active}
                                  onClick={() => handleRecalcOne(order)}
                                  title="Recalcular data de embarque"
                                >
                                  {loadingThisRow ? '…' : '↻ Recalcular'}
                                </button>
                              )}
                            </span>
                          </p>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      <div className={styles.mobile}>
        <h1>EXPEDIR HOJE</h1>
        <div>
          <h2>AG. INÍCIO</h2>
          <p>{totals.ordersClosed}</p>
        </div>
        <div>
          <h2>EM SEPARAÇÃO</h2>
          <p>{totals.ordersInPicking}</p>
        </div>
        <div>
          <h2>AG. EMBALAGEM</h2>
          <p>{totals.ordersAwaitingPackaging}</p>
        </div>
        <div>
          <h2>AG. EMBARQUE</h2>
          <p>{totals.ordersAwaitingManifest}</p>
        </div>
        <div className="full">
          <h2>TOTAL PENDENTE</h2>
          <p>
            {totals.ordersAwaitingManifest +
              totals.ordersAwaitingPackaging +
              totals.ordersClosed +
              totals.ordersInPicking}
          </p>
        </div>
        <div className="delayed">
          <h2>ATRASADOS</h2>
          <p>{totals.ordersDelayed}</p>
        </div>
        <div className="forecast">
          <h2>{forecastLabel}</h2>
          <p>{totals.ordersNextDay}</p>
        </div>
        <h1 className="shipped">EXPEDIDOS HOJE</h1>
        <div className="earlyShipping">
          <h2>ANTECIPADOS</h2>
          <p>{totals.ordersEarlyShipping}</p>
        </div>
        <div className="lateShipping">
          <h2>EM ATRASO</h2>
          <p>{totals.ordersLateShipping}</p>
        </div>
        <div className="inTime">
          <h2>NO PRAZO</h2>
          <p>{totals.ordersOnTimeShipping}</p>
        </div>
        <div className="full">
          <h2>TOTAL ENVIADO</h2>
          <p>{totals.ordersShipped}</p>
        </div>
      </div>
    </>
  );
}

export default Embarque;
